import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { LangContext, useLang } from '../../contexts/LangContext.js';
import {
  LineChart, BarChart, AreaChart, RadarChart, ScatterChart,
  Line, Bar, Area, Radar, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
  ResponsiveContainer, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Cell, ComposedChart,
} from 'recharts';
import { COLORS_DARK, COLORS_LIGHT } from '../shared/theme.js';
import { useTheme } from '../../contexts/ThemeContext.js';
import { CB, CT, PI } from '../shared/helpers.jsx';
import { computeAutoTuning, computeCascadeAnalysis, computeControlSynthesis, computeCurrentLoopBode, computeDQCoupling, computeGainScheduling, computeHinf, computeMPC, computeMRAC, computeNyquistData, computeRootLocus, computeSpeedLoopBode, computeZDomainData, simAntiWindup, applyDriveModeGains, DRIVE_MODE_GAIN_TABLE, multiLoopGainOptimizer, optimizeDriveModeGainMap } from '../../core/control/motor_control.js';
import { computeMotorOperatingPointMap } from '../../core/physics/motor.js';
import { simSkidPad } from '../../core/physics/simulation.js';
import { YAxisToggleBtn, AxesPanel, useAxes, RLKpiBadgeRow } from '../shared/rl_widgets.jsx';
import { TorqueMap3D } from '../shared/advanced_widgets.jsx';
import { useColLayout } from '../../contexts/ColLayoutContext.js';

// COLORS: LangContext/ThemeContext 없이도 동작하는 런타임 참조
// V49 메인의 isDark 상태에 따라 동적으로 결정되나,
// 탭 파일은 일단 COLORS_LIGHT를 기본값으로 사용
// (isDark props가 필요하면 추후 ThemeContext로 교체)

// LangContext — V49 메인의 Provider 안에서 렌더되므로 동작함

// ============================================================
// ControlLoopTabContent.js
// EV Powertrain Simulator — Phase 4 컴포넌트 분리
// 제어 루프 탭
// ============================================================

// ══════════════════════════════════════════════════════════════
// §LIVE_ANIM  실시간 파라미터 슬라이더 애니메이션 정의  [v09 신규]
// ══════════════════════════════════════════════════════════════
// ── ControlLoopTabContent ──
function ControlLoopTabContent({ params, onParamChange }) {
  const COLORS = useTheme();

  const colLayout = useColLayout();  const lang = useLang();
  const e = (ko, en) => lang === 'en' ? en : ko;
  const _gc = colLayout === 1 ? '1fr' : colLayout === 3 ? '1fr 1fr 1fr' : colLayout === 4 ? '1fr 1fr 1fr 1fr' : '1fr 1fr';

  // ── CL: ControlLoop 시맨틱 팔레트 — COLORS 토큰 기반 ─────────────────────
  // 다크/라이트 테마 모두 자동 대응.  hex 리터럴 직접 사용 금지.
  const CL = React.useMemo(() => ({
    ok:      COLORS.accent3,        // ✅ 정상·안전·양수  (green  #3fb950 dark)
    warn:    COLORS.accent,         // ⚠ 경고·참조선     (amber  #f59e0b dark)
    err:     COLORS.thermalRed,     // ❌ 오류·위험·음수  (red    #f85149 dark)
    blue:    COLORS.thermalBlue,    // 📘 속도루프·2차축  (blue   #58a6ff dark)
    purple:  COLORS.gearColor,      // 🟣 게인·적응제어   (violet #bc8cff dark)
    orange:  COLORS.thermalOrange,  // 🟠 철손·위상여유   (orange #ff9500 dark)
    teal:    COLORS.accent3,        // 🔵 효율·냉각수     (teal   #3fb950 dark)
    pink:    COLORS.mechColor,      // 💗 대역폭(BW)      (pink   #d2a8ff dark)
    neutral: COLORS.auxColor,       // ⬜ 중립·규칙기반   (gray   #6e7681 dark)
    crit:    COLORS.thermalRed,     // 🔴 임계점·극점     (red    #f85149 dark)
    deep_g:  COLORS.motorColor,     // 🟢 deep-green      (#3fb950 dark)
    deep_p:  COLORS.gearColor,      // 🟣 deep-purple     (#bc8cff dark)
    // 반투명 배경 (badge bg 용)
    ok_bg:   COLORS.isDark ? CL.ok_bg : '#dcfce722',
    ok_b:    COLORS.isDark ? CL.ok_b : '#86efac40',
    err_bg:  COLORS.isDark ? CL.err_bg : '#fee2e222',
    err_b:   COLORS.isDark ? CL.err_b : '#fca5a540',
    warn_bg: COLORS.isDark ? CL.warn_bg : '#fef3c722',
    warn_b:  COLORS.isDark ? CL.warn_b : '#fcd34d40',
  }), [COLORS]);

  const axCL1 = useAxes();
  const axCL2 = useAxes();
  const axCL3 = useAxes();
  const axCL4 = useAxes();
  const axCL5 = useAxes();
  const axCL6 = useAxes();
  const axCL7 = useAxes();
  const axCL8 = useAxes();
  const axCL9 = useAxes();
  const axCL10 = useAxes();
  const axCL11 = useAxes();
  const axCL12 = useAxes();
  const axCL13 = useAxes();
  const axCL14 = useAxes();
  const axCL15 = useAxes();
  const axCL16 = useAxes();
  const axCL17 = useAxes();
  const axCL18 = useAxes();
  const axCL19 = useAxes();
  const axCL20 = useAxes();
  const axCL21 = useAxes();
  const axCL22 = useAxes();
  const axCL23 = useAxes();
  const mono = "'JetBrains Mono', monospace";
  const [sub,       setSub]       = React.useState('cascade');
  const [loopType,  setLoopType]  = React.useState('current');
  const [tuneMethod,setTuneMethod]= React.useState('imc');



  const displayParams = params;

  // ── 드라이브 모드 게인 적용 (ECO / NORMAL / SPORT / SNOW / OFF_ROAD) ──────────
  // drive_mode 또는 기본 Kp/Ki 변경 시 자동 재계산
  // 모든 제어 루프 계산은 effectiveParams 기반으로 수행됨
  const effectiveParams = React.useMemo(
    () => applyDriveModeGains(displayParams),
    [displayParams]
  );
  const driveMode = effectiveParams._dm_mode || 'NORMAL';
  const dmFactors = effectiveParams._dm_factors || DRIVE_MODE_GAIN_TABLE.NORMAL;

  // IMC 계수 계산 및 params에 기입
  // [FIX-IMC] computeAutoTuning returns { results:{imc,zn,simc,cc}, K_p, tau_p, ... }
  //           → 올바른 경로: result.results?.imc  (이전 코드: result?.imc → 항상 undefined → 기본값 고착)

  // [A34] SUB_TABS → 6개 카테고리 2단 구조
  const SUB_GROUPS = [
    {
      id: 'structure', icon: '🏗️', label: lang==='en'?'Loop Structure':'루프 구조',
      color: CL.neutral,
      tabs: [
        { id: 'cascade',  icon: '🏗️', label: lang==='en'?'Cascade Loops':'다중 루프 계층' },
        { id: 'synthesis',icon: '✅', label: lang==='en'?'Control Synthesis':'제어 합성 (종합)' },
      ],
    },
    {
      id: 'stability', icon: '📐', label: lang==='en'?'Stability Analysis':'안정성 분석',
      color: CL.blue,
      tabs: [
        { id: 'nyquist',   icon: '🌀', label: 'Nyquist' },
        { id: 'rootlocus', icon: '📍', label: lang==='en'?'Root Locus':'근궤적' },
        { id: 'zdomain',   icon: '🔢', label: lang==='en'?'Z-Domain':'이산(Z-Domain)' },
      ],
    },
    {
      id: 'tuning', icon: '🔧', label: lang==='en'?'Design & Tuning':'설계·튜닝',
      color: CL.ok,
      tabs: [
        { id: 'autotuning',   icon: '🎯', label: lang==='en'?'Auto PI Tuning':'자동 PI 튜닝' },
        { id: 'antiwindup',   icon: '⚙️', label: 'Anti-windup' },
        { id: 'gainschedule', icon: '📊', label: lang==='en'?'Gain Scheduling':'게인 스케줄링' },
      ],
    },
    {
      id: 'advanced', icon: '🧠', label: lang==='en'?'Advanced Control':'고급 제어',
      color: CL.orange,
      tabs: [
        { id: 'mrac', icon: '🔄', label: lang==='en'?'MRAC (Adaptive)':'MRAC 적응제어' },
        { id: 'hinf', icon: '🛡️', label: lang==='en'?'H∞ Robust':'H∞ 강인제어' },
        { id: 'mpc',  icon: '🔮', label: lang==='en'?'MPC Predictive':'MPC 예측제어' },
      ],
    },
    {
      id: 'motor', icon: '⚡', label: lang==='en'?'Motor Characteristics':'모터 특성',
      color: CL.pink,
      tabs: [
        { id: 'torque3d', icon: '🗺️', label: lang==='en'?'3D Torque Map':'3D 토크맵' },
        { id: 'coupling', icon: '🔗', label: lang==='en'?'d↔q Coupling':'d↔q 교차커플링' },
      ],
    },
    {
      id: 'vehicle', icon: '🏎️', label: lang==='en'?'Vehicle Dynamics':'차량 동역학',
      color: CL.purple,
      tabs: [
        { id: 'skidpad', icon: '⭕', label: lang==='en'?'Skid Pad':'스키드패드' },
      ],
    },
    {
      id: 'optimizer', icon: '🚀', label: lang==='en'?'Optimizer [RevD]':'최적화기 [RevD]',
      color: '#8b5cf6',
      tabs: [
        { id: 'multiloop',  icon: '🔀', label: lang==='en'?'Multi-Loop Opt.':'멀티루프 최적화' },
        { id: 'drivemode',  icon: '🎛️', label: lang==='en'?'Drive Mode Gain':'드라이브 모드 게인' },
        { id: 'oppoint',    icon: '🗺️', label: lang==='en'?'Motor Op. Map':'모터 동작점 맵' },
      ],
    },
  ];
  // ── 섹션 설명 맵 (섹션설명없음 개선) ──────────────────────────────
  const SUB_GROUP_DESC = {
    structure: {
      ko: '제어 루프의 계층 구조를 정의합니다. 전류(내부) → 속도(중간) → 위치(외부) 다중 루프 분리비(≥5×)를 확인하고, 모든 분석 결과를 종합하여 최적 Kp/Ki를 산출합니다.',
      en: 'Defines the hierarchical structure of control loops. Verify Current→Speed→Position cascade separation ratio (≥5×) and aggregate all analyses to compute optimal Kp/Ki.',
    },
    stability: {
      ko: '개루프 전달함수의 주파수 영역·복소 평면·이산 영역 안정성을 분석합니다. Nyquist(PM/GM), 근궤적(우반면 진입 방지), Z-Domain(단위원 내부 극점) 세 관점에서 안정 여유를 검증합니다.',
      en: 'Analyses open-loop stability in frequency, complex, and discrete domains. Verifies stability margins via Nyquist (PM/GM), Root Locus (RHP avoidance), and Z-Domain (unit-circle poles).',
    },
    tuning: {
      ko: '최적 PI 게인을 설계하고 운전 영역 전반에 적용합니다. 자동 PI 튜닝(IMC/ZN/SIMC/CC), 포화 오버슈트를 방지하는 Anti-windup, 그리고 RPM·부하·온도 구간별 게인 스케줄링을 제공합니다.',
      en: 'Designs optimal PI gains and applies them across the operating range. Provides Auto PI Tuning (IMC/ZN/SIMC/CC), Anti-windup for saturation overshoot prevention, and Gain Scheduling across RPM/load/temperature zones.',
    },
    advanced: {
      ko: '고급 제어 알고리즘을 분석합니다. MRAC(MIT 규칙 적응 게인), H∞(혼합 감도 강인제어), MPC(후퇴 지평 예측 제어) — 플랜트 불확실성·외란·제약 조건 처리에 활용합니다.',
      en: 'Analyses advanced control algorithms. MRAC (MIT-rule adaptive gain), H∞ (mixed-sensitivity robust control), MPC (receding-horizon predictive control) — for plant uncertainty, disturbance rejection, and constraint handling.',
    },
    motor: {
      ko: 'IPMSM 전자기 특성을 시각화합니다. 3D 토크맵(T vs RPM·Iq)으로 파워 한계 영역을 파악하고, d↔q 축 교차 커플링 전압 비율로 피드포워드 분리 보상이 필요한 구간을 확인합니다.',
      en: 'Visualises IPMSM electromagnetic characteristics. The 3D torque map (T vs RPM·Iq) identifies power-limit regions; d↔q cross-coupling voltage ratios pinpoint zones requiring feedforward decoupling compensation.',
    },
    vehicle: {
      ko: '정상 원선회(Skid Pad) 시뮬레이션으로 차량 횡방향 동역학을 분석합니다. 타이어 포화 한계 속도, 최대 횡G, 언더스티어 구배 Ku를 산출하여 EV 구동 토크 벡터링 설계에 활용합니다.',
      en: 'Analyses lateral vehicle dynamics via steady-state Skid Pad simulation. Computes tyre saturation limit speed, peak lateral G, and understeer gradient Ku for EV torque vectoring design.',
    },
  };

  // 현재 그룹 (sub로 역산)
  const activeSubGroup = SUB_GROUPS.find(g => g.tabs.some(t => t.id === sub)) || SUB_GROUPS[0];
  const handleSubGroupChange = (gid) => {
    const g = SUB_GROUPS.find(x => x.id === gid);
    if (g) setSub(g.tabs[0].id);
  };
  // 하위 탭 목록 (현재 그룹)
  const activeCLTabs = activeSubGroup.tabs;

  const LOOP_OPTS = [
    { id: 'current',  label: lang==='en'?'Current Loop (Iq)':'전류 루프 (Iq)' },
    { id: 'speed',    label: lang==='en'?'Speed Loop':'속도 루프' },
  ];

  const TUNE_METHODS = [
    { id: 'imc',  label: 'IMC' }, { id: 'zn', label: 'Ziegler-Nichols' },
    { id: 'simc', label: 'SIMC' }, { id: 'cc', label: 'Cohen-Coon' },
  ];

  // ── 계산 (메모, effectiveParams 기반 — 드라이브 모드 Kp/Ki 반영) ──
  const cascade   = React.useMemo(() => computeCascadeAnalysis(effectiveParams), [effectiveParams]);
  const nyquist   = React.useMemo(() => computeNyquistData(effectiveParams, loopType), [effectiveParams, loopType]);
  const rootlocus = React.useMemo(() => computeRootLocus(effectiveParams, loopType), [effectiveParams, loopType]);
  const zdomain   = React.useMemo(() => computeZDomainData(effectiveParams, loopType), [effectiveParams, loopType]);
  const antiwindup= React.useMemo(() => simAntiWindup(effectiveParams, loopType), [effectiveParams, loopType]);
  const autotuning= React.useMemo(() => computeAutoTuning(effectiveParams, loopType, tuneMethod), [effectiveParams, loopType, tuneMethod]); // [A32-BUG1] tuneMethod 의존성 추가
  // [R-12] 신규 제어 분석
  const mracData  = React.useMemo(() => computeMRAC(effectiveParams), [effectiveParams]);
  const hinfData  = React.useMemo(() => computeHinf(effectiveParams), [effectiveParams]);
  const mpcData   = React.useMemo(() => computeMPC(effectiveParams), [effectiveParams]);
  const dqData    = React.useMemo(() => computeDQCoupling(effectiveParams), [effectiveParams]);
  const [skidR, setSkidR]     = React.useState(30);
  const [gsAxis, setGsAxis]    = React.useState('rpm'); // [A33] gain scheduling axis (classic view)
  // [V44] 3D 보간 게인 테이블 state
  const [gsView,    setGsView]    = React.useState('3d');
  const [gs3dX,     setGs3dX]     = React.useState('rpm');
  const [gs3dY,     setGs3dY]     = React.useState('torque');
  const [gs3dZIdx,  setGs3dZIdx]  = React.useState(2);
  const [gsMetric,  setGsMetric]  = React.useState('Kp_i');
  const [gsProbeRpm,  setGsProbeRpm]  = React.useState(4500);
  const [gsProbeTrq,  setGsProbeTrq]  = React.useState(0.5);
  const [gsProbeTemp, setGsProbeTemp] = React.useState(25);
  const [hideY2_rv_deg, set_hideY2_rv_deg] = React.useState(false); // [FIX] skidpad 2축 토글
  const skidData    = React.useMemo(() => simSkidPad(effectiveParams, skidR), [effectiveParams, skidR]);
  const gainSchedData = React.useMemo(() => computeGainScheduling(effectiveParams), [effectiveParams]); // [A33]
  // [A34] Control Synthesis — 모든 분석 결과 종합
  // [Fix-Hinf-C1] hinfData를 computeControlSynthesis에 전달 (기존: 미전달 → 스코어 단절)
  const synthData = React.useMemo(() =>
    computeControlSynthesis(effectiveParams, cascade, nyquist, autotuning, antiwindup, zdomain, gainSchedData, hinfData),
    [effectiveParams, cascade, nyquist, autotuning, antiwindup, zdomain, gainSchedData, hinfData]
  );

  const SC = {
    card: { background: COLORS.cardBg, borderRadius: '10px', padding: '12px 14px', border: `1px solid ${COLORS.border}`, marginBottom: '12px' },
    kpi:  { background: COLORS.cardBg, borderRadius: '8px', padding: '9px 12px', border: `1px solid ${COLORS.border}`, textAlign: 'center' },
    badge:(col, ok) => ({ display:'inline-block', padding:'2px 8px', borderRadius:'12px',
                          background: ok ? CL.ok_bg : CL.err_bg, color: ok ? CL.ok : CL.err,
                          fontWeight: 700, fontSize: '11px' }),
    subBtn: (active) => ({ padding:'6px 12px', borderRadius:'6px', border:'none', cursor:'pointer', fontSize:'11px', fontWeight:600,
                           background: active ? COLORS.accent : COLORS.border,
                           color: active ? '#fff' : COLORS.textMuted, marginRight: '4px' }),
    loopBtn:(active) => ({ padding:'4px 10px', borderRadius:'6px', border:'none', cursor:'pointer', fontSize:'11px', fontWeight:600,
                           background: active ? CL.deep_p : COLORS.border,
                           color: active ? '#fff' : COLORS.textMuted, marginRight: '4px' }),
  };

  // ─── 공통 루프 선택 컨트롤 ────────────────────────────────
  const LoopSelector = () => (
    <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'10px' }}>
      <span style={{ fontSize:'11px', color:COLORS.textMuted, marginRight:'4px' }}>
        {lang==='en'?'Loop:':'루프:'}
      </span>
      {LOOP_OPTS.map(o => (
        <button key={o.id} style={SC.loopBtn(loopType===o.id)} onClick={()=>setLoopType(o.id)}>{o.label}</button>
      ))}
    </div>
  );

  // ── 값 포맷 헬퍼 ────────────────────────────────────────────
  const fmtHz = v => v >= 1000 ? `${(v/1000).toFixed(1)} kHz` : v >= 1 ? `${v.toFixed(0)} Hz` : `${v.toFixed(3)} Hz`;

  // [A19] 탭별 목적 가이드 맵
  const TAB_PURPOSE = {
    cascade:     { icon:'🏗️', ko:'전류·속도·위치 3중 루프 분리비(≥5×) 만족 여부 확인. 내부 루프 먼저 튜닝 (전류→속도→위치)', en:'Verify loop separation ratio ≥5× across Current/Speed/Position. Tune innermost loop first.' },
    synthesis:   { icon:'✅', ko:'전체 분석 결과 종합 → 신뢰도 점수 → 추천 Kp/Ki 산출 → 제어기 적용 안내', en:'Aggregate all analyses → composite score → recommended Kp/Ki → apply guidance.' },
    nyquist:     { icon:'🌀', ko:'개루프 위상여유(PM)·이득여유(GM) 확인. PM ≥ 45°, GM ≥ 6 dB 목표', en:'Verify open-loop PM & GM. Target PM ≥ 45°, GM ≥ 6 dB.' },
    rootlocus:   { icon:'📍', ko:'Kp 증가에 따른 폐루프 극점 이동 시각화. 우반면 진입 전 한계 Kp 확인', en:'Visualize closed-loop pole migration vs Kp. Identify max safe Kp before RHP entry.' },
    zdomain:     { icon:'🔢', ko:'이산 안정성 확인 — 폐루프 극점이 단위원(z-plane) 내부에 있는지 검증', en:'Discrete stability: verify all closed-loop poles lie inside the unit circle.' },
    antiwindup:  { icon:'⚙️', ko:'역산법 Anti-windup 유무에 따른 오버슈트·정착시간 비교. 포화 발생 구간에서 효과 큼', en:'Compare overshoot/settling with/without back-calculation anti-windup. Most effective near saturation.' },
    autotuning:  { icon:'🎯', ko:'IMC · ZN · SIMC · Cohen-Coon 4가지 방법으로 최적 Kp/Ki 추천. IMC(λ)가 일반적으로 가장 안정적', en:'Recommend Kp/Ki via IMC, ZN, SIMC, Cohen-Coon. IMC(λ) is typically most robust.' },
    gainschedule:{ icon:'📊', ko:'속도·부하·온도 구간별 Lq 포화·Rs(T) 반영 최적 게인 산출. 넓은 운전 영역 커버', en:'Compute zone-optimal gains reflecting Lq saturation & Rs(T). Covers wide operating range.' },
    torque3d:    { icon:'🗺️', ko:'IPMSM 전자기 토크 T(RPM, Iq) 3D 서피스 + 파워 한계 영역 시각화', en:'3D electromagnetic torque surface T(RPM, Iq) with power-limit region (WebGL).' },
    mrac:        { icon:'🔄', ko:'MIT 규칙 적응 게인 — 플랜트 불일치에도 기준 모델 추종 시뮬레이션', en:'MIT-rule adaptive gain: tracks reference model despite plant parameter mismatch.' },
    hinf:        { icon:'🛡️', ko:'혼합 감도 H∞ 노름 — 불확실성·외란에 대한 강인성 정량화', en:'Mixed-sensitivity H∞ norm: quantify robustness against uncertainty & disturbance.' },
    mpc:         { icon:'🔮', ko:'후퇴 지평 속도 추종 MPC — ΔU 제약 포함 간소화 QP 최적화', en:'Receding-horizon MPC for velocity tracking with ΔU constraints (simplified QP).' },
    coupling:    { icon:'🔗', ko:'RPM별 교차 커플링 전압(ωe·Lq·Iq, ωe·Ld·Id) — 분리 보상 필요 구간 확인', en:'Cross-coupling voltages vs RPM: identify regions needing feedforward decoupling.' },
    skidpad:     { icon:'⭕', ko:'정상 원선회 분석 — 타이어 포화 한계, 언더스티어 구배 Ku 산출', en:'Steady-state circular cornering: tyre saturation limit, understeer gradient Ku.' },
  };
  const CurPurpose = () => {
    const p = TAB_PURPOSE[sub];
    if (!p) return null;
    return (
      <div style={{ marginBottom:'10px', padding:'7px 12px', borderRadius:'6px',
        background:`${activeSubGroup.color}0e`, border:`1px solid ${activeSubGroup.color}30`,
        display:'flex', gap:'8px', alignItems:'flex-start' }}>
        <span style={{ fontSize:'14px', flexShrink:0 }}>{p.icon}</span>
        <span style={{ fontSize:'10px', color:COLORS.textDim, lineHeight:1.65 }}>
          {lang==='en' ? p.en : p.ko}
        </span>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: COLORS.font || 'sans-serif' }}>
      {/* ── 적합 파라미터 / 영향 ── */}
      <CLFitParamPanel params={effectiveParams} COLORS={COLORS} lang={lang} />

      {/* ── 상단 KPI 요약 ── */}
      <RLKpiBadgeRow items={[
        [lang==='en'?'Synthesis Score':'종합 점수',
          synthData.score, '/100', synthData.score>=80?CL.ok:synthData.score>=60?CL.warn:CL.err],
        [lang==='en'?'Current BW':'전류 BW',
          cascade.current.BW_hz.toFixed(1), 'Hz', CL.err],
        [lang==='en'?'Speed BW':'속도 BW',
          cascade.speed.BW_hz.toFixed(1), 'Hz', CL.blue],
        [lang==='en'?'BW Sep. Ratio':'BW 분리비',
          cascade.sep_si + '×', '', cascade.sep_ok_si?CL.ok:CL.err],
        [lang==='en'?'Phase Margin (Iq)':'위상 여유 (Iq)',
          cascade.current.PM.toFixed(1), '°', cascade.current.PM>30?CL.ok:cascade.current.PM>10?CL.warn:CL.err],
        ['Kp_iq', effectiveParams.Kp_iq?.toFixed(2) ?? '—', '', CL.purple],
        ['Ki_iq', effectiveParams.Ki_iq?.toFixed(0) ?? '—', '', CL.purple],
        ['Kp_spd', effectiveParams.Kp_spd?.toFixed(2) ?? '—', '', CL.blue],
      ]} />

      {/* Level-1: 카테고리 */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'3px', marginBottom:0,
        padding:'6px 8px', background:COLORS.panel, borderRadius:'8px 8px 0 0',
        borderBottom:`1px solid ${COLORS.border}` }}>
        {SUB_GROUPS.map(g => (
          <button key={g.id} onClick={()=>handleSubGroupChange(g.id)}
            style={{ padding:'5px 12px', borderRadius:'5px 5px 0 0', fontSize:'10px', fontWeight:700,
              cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.12s',
              background: activeSubGroup.id===g.id ? COLORS.bg : 'transparent',
              color: activeSubGroup.id===g.id ? g.color : COLORS.textMuted,
              border: `1px solid ${activeSubGroup.id===g.id ? COLORS.border : 'transparent'}`,
              borderBottom: activeSubGroup.id===g.id ? `1px solid ${COLORS.bg}` : 'none',
              marginBottom: activeSubGroup.id===g.id ? '-1px' : 0 }}>
            {g.icon} {g.label}
          </button>
        ))}
      </div>
      {/* Level-2: 현재 카테고리의 서브탭 */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'3px', marginBottom:'14px',
        padding:'5px 8px', background:COLORS.bg,
        borderLeft:`3px solid ${activeSubGroup.color}`,
        borderBottom:`1px solid ${COLORS.border}` }}>
        {activeCLTabs.map(s => (
          <button key={s.id} style={SC.subBtn(sub===s.id)} onClick={()=>setSub(s.id)}>
            {s.icon} {s.label}
          </button>
        ))}
      </div>


      {/* ── 섹션 설명 배너 (섹션설명없음 개선) ── */}
      {SUB_GROUP_DESC[activeSubGroup.id] && (
        <div style={{ marginBottom:'10px', padding:'7px 13px',
          background:`${activeSubGroup.color}08`,
          border:`1px solid ${activeSubGroup.color}25`,
          borderRadius:'6px', fontSize:'10px', color:COLORS.textMuted, lineHeight:1.65 }}>
          <span style={{ fontWeight:700, color:activeSubGroup.color, marginRight:'6px' }}>
            {activeSubGroup.icon} {activeSubGroup.label}
          </span>
          {lang==='en' ? SUB_GROUP_DESC[activeSubGroup.id].en : SUB_GROUP_DESC[activeSubGroup.id].ko}
        </div>
      )}

      {/* ══════ [A34] Control Synthesis — 제어 합성 종합 ══════ */}
      {sub === 'synthesis' && (() => {
        const sy = synthData;
        const scoreColor = sy.score >= 80 ? CL.ok : sy.score >= 60 ? CL.warn : CL.err;
        const scoreLabel = sy.score >= 80
          ? (lang==='en'?'✓ Recommended to Apply':'✓ 적용 권장')
          : sy.score >= 60
          ? (lang==='en'?'⚠ Review Before Applying':'⚠ 검토 후 적용')
          : (lang==='en'?'✗ Significant Issues Found':'✗ 중요 문제 발견');
        // [A10] 현재 vs 추천 최대 변화율
        const maxDelta = Math.max(
          sy.current.Kp_iq  ? Math.abs(sy.recommended.Kp_iq  / sy.current.Kp_iq  - 1) : 0,
          sy.current.Ki_iq  ? Math.abs(sy.recommended.Ki_iq  / sy.current.Ki_iq  - 1) : 0,
          sy.current.Kp_spd ? Math.abs(sy.recommended.Kp_spd / sy.current.Kp_spd - 1) : 0,
          sy.current.Ki_spd ? Math.abs(sy.recommended.Ki_spd / sy.current.Ki_spd - 1) : 0,
        );
        const gainsAligned = maxDelta < 0.02; // 2% 이내면 이미 적용된 것으로 간주

        const STATUS_STYLE = {
          ok:   { bg:CL.ok_bg, color:CL.ok,  border:CL.ok_b, label: lang==='en'?'✓ OK':'✓ 정상' },
          warn: { bg:CL.warn_bg, color:CL.orange,  border:CL.warn_b, label: lang==='en'?'⚠ Warn':'⚠ 주의' },
          fail: { bg:CL.err_bg, color:CL.err,  border:CL.err_b, label: lang==='en'?'✗ Fail':'✗ 문제' },
        };

        return (
          <div>
            <CurPurpose />
            {/* 종합 점수 헤더 */}
            <div style={{ display:'flex', gap:'16px', alignItems:'center', marginBottom:'16px',
              padding:'12px 16px', borderRadius:'10px', background:COLORS.cardBg,
              border:`2px solid ${scoreColor}40` }}>
              {/* 원형 점수 */}
              <div style={{ flexShrink:0, width:72, height:72, borderRadius:'50%',
                border:`4px solid ${scoreColor}`, display:'flex', flexDirection:'column',
                alignItems:'center', justifyContent:'center',
                background:`${scoreColor}12` }}>
                <div style={{ fontSize:'22px', fontWeight:900, color:scoreColor }}>{sy.score}</div>
                <div style={{ fontSize:'8px', color:COLORS.textMuted }}>/ 100</div>
              </div>
              <div>
                <div style={{ fontSize:'14px', fontWeight:700, color:scoreColor }}>{scoreLabel}</div>
                <div style={{ fontSize:'10px', color:COLORS.textMuted, marginTop:'4px', lineHeight:1.6 }}>
                  {lang==='en'
                    ? 'Composite score based on Phase Margin, Loop Separation, Discrete Stability, Anti-windup Overshoot, Gain Schedule Min PM.'
                    : '위상 여유 · 루프 분리비 · 이산 안정성 · Anti-windup 오버슈트 · 게인 스케줄 최소 PM 을 종합한 점수입니다.'}
                </div>
              </div>
            </div>

            {/* 항목별 신호등 */}
            <div style={{ fontSize:'11px', fontWeight:600, color:COLORS.text, marginBottom:'8px' }}>
              {lang==='en'?'Design Criteria Check':'설계 기준 체크리스트'}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:GRID.auto260, gap:'8px', marginBottom:'16px' }}>
              {sy.items.map(item => {
                const st = STATUS_STYLE[item.status];
                return (
                  <div key={item.id} style={{ padding:'8px 12px', borderRadius:'8px',
                    background:st.bg, border:`1px solid ${st.border}`,
                    display:'flex', alignItems:'center', gap:'10px' }}>
                    <span style={{ fontSize:'18px' }}>
                      {item.status==='ok'?'🟢': item.status==='warn'?'🟡':'🔴'}
                    </span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'10px', fontWeight:700, color:st.color }}>
                        {lang==='en' ? item.labelEn : item.label}
                        <span style={{ marginLeft:'8px', padding:'1px 6px', borderRadius:'8px',
                          background:st.border, fontSize:'10px' }}>{item.value}</span>
                      </div>
                      <div style={{ fontSize:'9px', color:COLORS.textMuted, marginTop:'2px' }}>{item.detail}</div>
                    </div>
                    <span style={{ fontSize:'10px', fontWeight:700, color:st.color, whiteSpace:'nowrap' }}>{st.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Before / After 비교표 */}
            <div style={{ fontSize:'11px', fontWeight:600, color:COLORS.text, marginBottom:'8px' }}>
              {lang==='en'?'Current vs Recommended Gains':'현재 설정 vs 추천 게인 비교'}
            </div>
            <div style={{ overflowX:'auto', marginBottom:'16px' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px',
                fontFamily:"'JetBrains Mono', monospace" }}>
                <thead>
                  <tr style={{ background:COLORS.panel }}>
                    {(lang==='en'?['Parameter','Current','Recommended','Change']:['파라미터','현재 설정','추천값','변화']).map(h=>(
                      <th key={h} style={{ padding:'6px 12px', color:COLORS.textMuted, fontWeight:600,
                        textAlign:'center', borderBottom:`1px solid ${COLORS.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { param: lang==='en'?'Kp_iq (Current)':'Kp_iq (전류)', cur: sy.current.Kp_iq,  rec: sy.recommended.Kp_iq,  col:CL.blue },
                    { param: lang==='en'?'Ki_iq (Current)':'Ki_iq (전류)', cur: sy.current.Ki_iq,  rec: sy.recommended.Ki_iq,  col:CL.purple },
                    { param: lang==='en'?'Kp_spd (Speed)':'Kp_spd (속도)', cur: sy.current.Kp_spd, rec: sy.recommended.Kp_spd, col:CL.teal },
                    { param: lang==='en'?'Ki_spd (Speed)':'Ki_spd (속도)', cur: sy.current.Ki_spd, rec: sy.recommended.Ki_spd, col:CL.orange },
                  ].map((row,i)=>{
                    const delta = row.cur ? ((row.rec/row.cur-1)*100).toFixed(1) : '—';
                    const dNum  = parseFloat(delta);
                    const same  = Math.abs(dNum) < 1;
                    return (
                      <tr key={i} style={{ background: i%2===0?COLORS.bg:COLORS.cardBg }}>
                        <td style={{ padding:'6px 12px', color:row.col, fontWeight:700 }}>{row.param}</td>
                        <td style={{ padding:'6px 12px', color:COLORS.textMuted, textAlign:'right' }}>{row.cur}</td>
                        <td style={{ padding:'6px 12px', color:row.col, fontWeight:700, textAlign:'right' }}>{row.rec}</td>
                        <td style={{ padding:'6px 12px', textAlign:'right',
                          color: same?COLORS.textMuted: dNum>0?CL.ok:CL.err, fontWeight:700 }}>
                          {same ? (lang==='en' ? '≈ Same' : '≈ 동일') : `${dNum>0?'+':''}${delta}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* [A10] 게인 정렬 상태 표시 */}
            {gainsAligned && (
              <div style={{ marginBottom:'8px', padding:"6px 14px", borderRadius:'6px',
                background:CL.ok_bg, border:"1px solid #16a34a50",
                fontSize:'10px', color:CL.ok, fontWeight:700 }}>
                ✓ {lang==='en'
                  ? "Controller gains are already aligned with recommendations (< 2% deviation)"
                  : "현재 제어기 게인이 추천값과 일치합니다 (편차 < 2%)"}
              </div>
            )}
            {/* [A09] 제어기 즉시 적용 패널 */}
            {(() => {
              const canApply = sy.score >= 60;
              const applyAll = () => {
                onParamChange('Kp_iq',  sy.recommended.Kp_iq);
                onParamChange('Ki_iq',  sy.recommended.Ki_iq);
                onParamChange('Kp_spd', sy.recommended.Kp_spd);
                onParamChange('Ki_spd', sy.recommended.Ki_spd);
              };
              const applyCurrent = () => {
                onParamChange('Kp_iq', sy.recommended.Kp_iq);
                onParamChange('Ki_iq', sy.recommended.Ki_iq);
              };
              const applySpeed = () => {
                onParamChange('Kp_spd', sy.recommended.Kp_spd);
                onParamChange('Ki_spd', sy.recommended.Ki_spd);
              };
              const revertAll = () => {
                onParamChange('Kp_iq',  sy.current.Kp_iq);
                onParamChange('Ki_iq',  sy.current.Ki_iq);
                onParamChange('Kp_spd', sy.current.Kp_spd);
                onParamChange('Ki_spd', sy.current.Ki_spd);
              };
              return (
                <div style={{ padding:'12px 16px', borderRadius:'8px', marginBottom:'12px',
                  background: canApply ? CL.ok_bg : CL.err_bg,
                  border:`1px solid ${canApply ? CL.ok_b : CL.err_b}` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'11px', fontWeight:700, color: canApply ? CL.ok : CL.err, flex:1 }}>
                      {canApply
                        ? (lang==='en' ? '✓ Ready to apply — score sufficient' : `✓ 적용 가능 — 신뢰도 ${sy.score}점`)
                        : (lang==='en' ? '⚠ Low score — review issues before applying' : `⚠ 신뢰도 낮음 (${sy.score}점) — 문제 확인 후 적용`)}
                    </span>
                    {/* 전체 적용 */}
                    <button onClick={applyAll}
                      disabled={!onParamChange}
                      title={!canApply ? (lang==='en'?'Score < 60 — apply with caution':'점수 < 60 — 주의하여 적용') : ''}
                      style={{ padding:'7px 18px', borderRadius:'6px', cursor:'pointer', fontWeight:700, fontSize:'11px',
                        background: canApply ? CL.deep_g : CL.neutral,
                        color:'#fff', border:'none', opacity: canApply ? 1 : 0.7 }}>
                      ⚡ {lang==='en' ? 'Apply All' : '전체 적용'}
                    </button>
                    {/* 되돌리기 */}
                    <button onClick={revertAll}
                      disabled={!onParamChange}
                      style={{ padding:'7px 14px', borderRadius:'6px', cursor:'pointer', fontWeight:600, fontSize:'11px',
                        background:'transparent', color:CL.neutral,
                        border:'1px solid #334155' }}>
                      ↩ {lang==='en' ? 'Revert' : '되돌리기'}
                    </button>
                  </div>
                  {/* 개별 적용 버튼 */}
                  <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                    <button onClick={applyCurrent}
                      disabled={!onParamChange}
                      style={{ padding:'5px 14px', borderRadius:'5px', cursor:'pointer', fontWeight:600, fontSize:'10px',
                        background:'${CL.blue}20', color:CL.blue, border:'1px solid ${CL.blue}50' }}>
                      🔌 {lang==='en'
                        ? `Current Loop: Kp=${sy.recommended.Kp_iq}  Ki=${sy.recommended.Ki_iq}`
                        : `전류 루프 적용: Kp=${sy.recommended.Kp_iq}  Ki=${sy.recommended.Ki_iq}`}
                    </button>
                    <button onClick={applySpeed}
                      disabled={!onParamChange}
                      style={{ padding:'5px 14px', borderRadius:'5px', cursor:'pointer', fontWeight:600, fontSize:'10px',
                        background:'${CL.teal}20', color:CL.teal, border:'1px solid ${CL.teal}50' }}>
                      🔄 {lang==='en'
                        ? `Speed Loop: Kp=${sy.recommended.Kp_spd}  Ki=${sy.recommended.Ki_spd}`
                        : `속도 루프 적용: Kp=${sy.recommended.Kp_spd}  Ki=${sy.recommended.Ki_spd}`}
                    </button>
                  </div>
                  {!canApply && (
                    <div style={{ marginTop:'8px', fontSize:'9px', color:CL.err, lineHeight:1.6 }}>
                      ⚠ {lang==='en'
                        ? 'Score below 60 — check items marked Fail/Warn above before applying.'
                        : '신뢰도 60 미만 — Fail/Warn 항목을 먼저 해결한 후 적용을 권장합니다.'}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* 상세 수치 요약 */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'6px' }}>
              {[
                {label:lang==='en'?'Current PM':'전류 PM',     val:`${sy.pm_i.toFixed(1)}°`, col: sy.pm_i>=45?CL.ok:sy.pm_i>=30?CL.warn:CL.err},
                {label:lang==='en'?'Speed PM':'속도 PM',       val:`${sy.pm_s.toFixed(1)}°`, col: sy.pm_s>=45?CL.ok:sy.pm_s>=30?CL.warn:CL.err},
                {label:lang==='en'?'Separation':'분리비',      val:`${sy.sep_si}×`,          col: sy.sep_si>=5?CL.ok:sy.sep_si>=3?CL.warn:CL.err},
                {label:lang==='en'?'Z Stable':'이산 안정',     val:sy.zd_stable?'✓':'✗',    col: sy.zd_stable?CL.ok:CL.err},
                {label:lang==='en'?'GS Min PM':'GS 최소 PM',  val:`${sy.gs_min_pm.toFixed(1)}°`, col:sy.gs_min_pm>=40?CL.ok:sy.gs_min_pm>=30?CL.warn:CL.err},
              ].map(k=>(
                <div key={k.label} style={{...SC.kpi, borderTop:`3px solid ${k.col}`}}>
                  <div style={{fontSize:'9px', color:COLORS.textMuted}}>{k.label}</div>
                  <div style={{fontSize:'15px', fontWeight:800, color:k.col}}>{k.val}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ══════ [1] 다중 루프 계층 ══════ */}
      {sub === 'cascade' && (() => {
        const c = cascade;
        const rows = [
          { name: lang==='en'?'Inner: Current Loop (Iq/Id)':'내부: 전류 루프 (Iq/Id)', bw: c.current.BW_hz, pm: c.current.PM, kp: c.current.Kp, ki: c.current.Ki, kd: 0,       col: CL.err },
          { name: lang==='en'?'Middle: Speed Loop':'중간: 속도 루프',                  bw: c.speed.BW_hz,   pm: c.speed.PM,   kp: c.speed.Kp,   ki: c.speed.Ki,   kd: 0,       col: CL.blue },
          { name: lang==='en'?'Outer: Position Loop (PID)':'외부: 위치 루프 (PID)',    bw: c.position.BW_hz,pm: c.position.PM,kp: c.position.Kp,ki: c.position.Ki||0, kd: c.position.Kd||0, col: CL.ok },
        ];
        return (
          <div>
            <CurPurpose />
            {/* KPI 카드 */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'14px' }}>
              {rows.map(row => (
                <div key={row.name} style={{ ...SC.card, borderLeft:`3px solid ${row.col}` }}>
                  <div style={{ fontSize:'10px', color:COLORS.textMuted, marginBottom:'4px' }}>{row.name}</div>
                  <div style={{ fontSize:'15px', fontWeight:800, color:row.col }}>{fmtHz(row.bw)}</div>
                  <div style={{ fontSize:'10px', color:COLORS.textMuted, marginTop:'3px' }}>
                    PM: <span style={{ fontWeight:700, color: row.pm>30 ? CL.ok : row.pm>10 ? CL.warn : CL.err }}>{row.pm.toFixed(1)}°</span>
                    &nbsp;| Kp: {row.kp.toFixed(2)} | Ki: {row.ki.toFixed(0)}{row.kd > 0 ? ` | Kd: ${row.kd.toFixed(3)}` : ''}
                  </div>
                </div>
              ))}
            </div>

            {/* BW 분리비 */}
            <div key={colLayout} style={{ display:'grid', gridTemplateColumns:_gc, gap:'8px', marginBottom:'14px' }}>
              {[
                { label: lang==='en'?'Current / Speed BW ratio':'전류/속도 BW 비', val: c.sep_si, ok: c.sep_ok_si, thr: 5 },
                { label: lang==='en'?'Speed / Position BW ratio':'속도/위치 BW 비', val: c.sep_ps, ok: c.sep_ok_ps, thr: 5 },
              ].map(item => (
                <div key={item.label} style={SC.card}>
                  <div style={{ fontSize:'10px', color:COLORS.textMuted }}>{item.label}</div>
                  <div style={{ fontSize:'20px', fontWeight:800, color: item.ok?CL.ok:CL.err }}>{item.val}×</div>
                  <div style={{ fontSize:'10px', color:COLORS.textMuted }}>
                    {lang==='en'?`Recommend ≥${item.thr}×`:`권장 ≥${item.thr}×`}&nbsp;
                    <span style={SC.badge(null, item.ok)}>{item.ok?(lang==='en'?'OK':'적정'):(lang==='en'?'TOO CLOSE':'간격 부족')}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* 주파수 응답 그래프 */}
            <div style={{ fontSize:'11px', fontWeight:600, color:COLORS.text, marginBottom:'6px' }}>
              {lang==='en'?'Closed-loop Bandwidth Comparison (Bode Magnitude)':'폐루프 대역폭 비교 (보드 이득)'}
            </div>
            <CB width="100%" height={240} chartId="CL1">
              <LineChart data={c.freqRows} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                <XAxis dataKey="f_hz" stroke={COLORS.textMuted} tick={{fontSize:'9px'}} scale="log" type="number"
                  domain={['dataMin','dataMax']} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v.toFixed(1)}
                  label={{value:lang==='en'?'Frequency (Hz)':'주파수 (Hz)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}} />
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:'Gain (dB)',angle:-90,position:'insideLeft',dx:10,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}} />
                <Tooltip content={<CT />} />
                <Legend wrapperStyle={{fontSize:'10px'}} />
                <ReferenceLine y={-3} stroke={CL.warn} strokeDasharray="4 2" strokeWidth={1} label={{value:'-3dB',fill:CL.warn,fontSize:'9px'}} />
                {/* 전류 루프 Cutoff Frequency 수직선 */}
                {c.current?.BW_hz && (
                  <ReferenceLine x={c.current.BW_hz} stroke={CL.err} strokeDasharray="3 2" strokeWidth={1.5}
                    label={{value: lang==='en'
                      ? `I-BW ${c.current.BW_hz>=1000?(c.current.BW_hz/1000).toFixed(1)+'kHz':c.current.BW_hz+'Hz'}`
                      : `전류BW ${c.current.BW_hz>=1000?(c.current.BW_hz/1000).toFixed(1)+'kHz':c.current.BW_hz+'Hz'}`,
                      fill:CL.err, fontSize:'8px', position:'insideTopRight'}} />
                )}
                {/* 속도 루프 Cutoff Frequency 수직선 */}
                {c.speed?.BW_hz && (
                  <ReferenceLine x={c.speed.BW_hz} stroke={CL.blue} strokeDasharray="3 2" strokeWidth={1.5}
                    label={{value: lang==='en'
                      ? `S-BW ${c.speed.BW_hz>=1?(c.speed.BW_hz).toFixed(1)+'Hz':((c.speed.BW_hz)*1000).toFixed(1)+'mHz'}`
                      : `속도BW ${c.speed.BW_hz>=1?(c.speed.BW_hz).toFixed(1)+'Hz':((c.speed.BW_hz)*1000).toFixed(1)+'mHz'}`,
                      fill:CL.blue, fontSize:'8px', position:'insideTopLeft'}} />
                )}
                <Line type="monotone" dataKey="Ti_dB" stroke={CL.err} strokeWidth={2} dot={false} name={lang==='en'?'Current CL (dB)':'전류 CL (dB)'} />
                <Line type="monotone" dataKey="Ts_dB" stroke={CL.blue} strokeWidth={2} dot={false} name={lang==='en'?'Speed CL (dB)':'속도 CL (dB)'} />
                <Line type="monotone" dataKey="Tp_dB" stroke={CL.ok} strokeWidth={2} dot={false} name={lang==='en'?'Position CL (dB)':'위치 CL (dB)'} />
              </LineChart>
            </CB>

            {/* 노하우 */}
            <div style={{ marginTop:'12px', padding:'10px 14px', background:COLORS.cardBg, borderRadius:'8px', fontSize:'10px', color:COLORS.textMuted, lineHeight:1.7 }}>
              <strong style={{color:COLORS.text}}>📐 {lang==='en'?'Cascade Control Design Rules':'캐스케이드 제어 설계 원칙'}</strong><br/>
              {lang==='en'
                ? '① Inner loop BW ≫ outer loop BW (≥5× separation recommended)  ② Tune inner loop first (current → speed → position)  ③ PM > 45° ensures robustness  ④ If separation ratio <5×, consider reducing outer Kp'
                : '① 내부 루프 BW ≫ 외부 루프 BW (≥5× 분리 권장)  ② 내부 루프 먼저 튜닝 (전류 → 속도 → 위치)  ③ PM > 45° 확보 시 강인성 보장  ④ 분리비 <5×이면 외부 Kp 감소 검토'}
            </div>
          </div>
        );
      })()}

      {/* ══════ [2] Nyquist 선도 ══════ */}
      {sub === 'nyquist' && (() => {
        const nq = nyquist;
        // split positive/negative freq for separate traces
        const posFreq = nq.curve.filter(d => parseFloat(d.w) >= 0);
        const negFreq = nq.curve.filter(d => parseFloat(d.w) < 0);
        return (
          <div>
            <CurPurpose />
            <LoopSelector />
            <div style={{ fontSize:'11px', color:COLORS.textMuted, marginBottom:'8px' }}>
              {lang==='en'
                ?'Nyquist plot: Re(L(jω)) vs Im(L(jω)). System is stable if the contour does NOT encircle the −1+0j point (red ✕).'
                :'Nyquist 선도: Re(L(jω)) vs Im(L(jω)). 등고선이 −1+0j (빨간 ✕)를 둘러싸지 않으면 안정.'}
            </div>
            <CB width="100%" height={340} chartId="CL2">
              <ScatterChart margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                <XAxis type="number" dataKey="re" domain={[-3,2]} stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:'Re(L(jω))',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}} />
                <YAxis allowDataOverflow type="number" dataKey="im" domain={[-3,3]} stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:'Im(L(jω))',angle:-90,position:'insideLeft',dx:10,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}} />
                <Tooltip cursor={{strokeDasharray:'3 3'}} content={({payload})=>{
                  if(!payload||!payload[0]) return null;
                  const d=payload[0].payload;
                  return <div style={{background:COLORS.cardBg,border:`1px solid ${COLORS.border}`,padding:'6px 10px',fontSize:'10px'}}>
                    Re: {d.re} | Im: {d.im} | ω: {d.w}
                  </div>;
                }} />
                {/* Unit circle */}
                <Scatter data={nq.unitCircle} fill="none" line={{stroke:'${CL.warn}88',strokeWidth:1,strokeDasharray:'3 3'}} shape={()=>null} name="Unit Circle" />
                {/* Nyquist curve (positive ω) */}
                <Scatter data={posFreq} fill={COLORS.accent} line={{stroke:COLORS.accent,strokeWidth:1.5}} shape={()=>null} name={lang==='en'?'루프 이득 (ω>0) (−)':'루프 이득 (ω>0) (−)'} />
                {/* Mirror (negative ω) */}
                <Scatter data={negFreq} fill={COLORS.accent2} line={{stroke:COLORS.accent2,strokeWidth:1,strokeDasharray:'2 2'}} shape={()=>null} name={lang==='en'?'루프 이득 반전 (ω<0) (−)':'루프 이득 반전 (ω<0) (−)'} />
                {/* Critical point -1+0j */}
                <Scatter data={[{re:-1,im:0}]} fill={CL.crit} shape={(props)=>{
                  const {cx,cy}=props;
                  return <g><line x1={cx-6} y1={cy-6} x2={cx+6} y2={cy+6} stroke={CL.crit} strokeWidth={2}/><line x1={cx+6} y1={cy-6} x2={cx-6} y2={cy+6} stroke={CL.crit} strokeWidth={2}/></g>;
                }} name="-1+0j (critical)" />
                {/* Origin */}
                <Scatter data={[{re:0,im:0}]} fill={CL.neutral} shape={(props)=>{const{cx,cy}=props;return <circle cx={cx} cy={cy} r={3} fill={CL.neutral}/>;}} name="Origin" />
              </ScatterChart>
            </CB>
            <div style={{marginTop:'8px',padding:'8px 12px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted}}>
              <strong style={{color:COLORS.text}}>{lang==='en'?'Reading Guide:':'판독 가이드:'}</strong>&nbsp;
              {lang==='en'
                ?'Solid line = positive frequencies (ω: 0→∞). Dashed = mirror (ω<0). Closer the curve to −1+0j, smaller the stability margin.'
                :'실선 = 양의 주파수(ω: 0→∞), 점선 = 대칭(ω<0). 등고선이 −1+0j에 가까울수록 안정 여유가 작음.'}
            </div>
          </div>
        );
      })()}

      {/* ══════ [3] 근궤적 ══════ */}
      {sub === 'rootlocus' && (() => {
        const rl = rootlocus;
        const pt1 = rl.loci.map(d => ({ x: d.r1, y: d.i1, k: d.k }));
        const pt2 = rl.loci.map(d => ({ x: d.r2, y: d.i2, k: d.k }));
        const pt3 = rl.loci.filter(d=>d.r3!=null).map(d => ({ x: d.r3, y: d.i3, k: d.k }));
        const poleMarkers = rl.poles.map(p => ({ x: p, y: 0 }));
        const zeroMarkers = rl.zeros.map(z => ({ x: z, y: 0 }));
        const xMin = Math.min(-1, ...rl.poles, ...rl.zeros) * 1.3;
        const xMax = 0.1;
        return (
          <div>
            <CurPurpose />
            <LoopSelector />
            <div style={{fontSize:'10px',color:COLORS.textMuted,marginBottom:'6px'}}>{rl.note}</div>
            <div style={{fontSize:'11px',color:COLORS.textMuted,marginBottom:'8px'}}>
              {lang==='en'
                ?'Root locus: closed-loop pole migration as gain K varies (0 → Kmax). Poles must remain in left half-plane (Re < 0) for stability.'
                :'근궤적: 이득 K 변화에 따른 폐루프 극점 이동. 모든 극점이 좌반면(Re < 0)에 있어야 안정.'}
            </div>
            <CB width="100%" height={320} chartId="CL3">
              <ScatterChart margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                <XAxis type="number" dataKey="x" domain={[xMin, xMax]} stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Real Axis [rad/s]':'실수축 [rad/s]',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}} />
                <YAxis type="number" dataKey="y" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Imaginary Axis':'허수축',angle:-90,position:'insideLeft',dx:10,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}} />
                <ReferenceLine x={0} stroke={CL.crit} strokeDasharray="3 3" strokeWidth={1.5} />
                <Tooltip content={({payload})=>{
                  if(!payload||!payload[0]) return null;
                  const d=payload[0].payload;
                  return <div style={{background:COLORS.cardBg,border:`1px solid ${COLORS.border}`,padding:'6px 10px',fontSize:'10px'}}>
                    K={d.k} | ({d.x?.toFixed(2)}, {d.y?.toFixed(2)}j)
                  </div>;
                }} />
                <Scatter data={pt1} line={{stroke:COLORS.accent,strokeWidth:1.5}} shape={()=>null} fill={COLORS.accent} name="Pole 1 locus" />
                <Scatter data={pt2} line={{stroke:COLORS.accent2,strokeWidth:1.5}} shape={()=>null} fill={COLORS.accent2} name="Pole 2 locus" />
                {pt3.length>0 && <Scatter data={pt3} line={{stroke:CL.ok,strokeWidth:1.5}} shape={()=>null} fill={CL.ok} name="Pole 3 locus" />}
                {/* Poles (×) */}
                <Scatter data={poleMarkers} fill={CL.crit} shape={(props)=>{
                  const{cx,cy}=props;
                  return <g><line x1={cx-7} y1={cy-7} x2={cx+7} y2={cy+7} stroke={CL.crit} strokeWidth={2.5}/><line x1={cx+7} y1={cy-7} x2={cx-7} y2={cy+7} stroke={CL.crit} strokeWidth={2.5}/></g>;
                }} name={lang==='en'?'개루프 극점 (−)':'개루프 극점 (−)'} />
                {/* Zeros (○) */}
                <Scatter data={zeroMarkers} fill="none" shape={(props)=>{
                  const{cx,cy}=props;
                  return <circle cx={cx} cy={cy} r={7} stroke={CL.warn} strokeWidth={2.5} fill="none"/>;
                }} name={lang==='en'?'개루프 영점 (−)':'개루프 영점 (−)'} />
              </ScatterChart>
            </CB>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px',marginTop:'10px'}}>
              {[
                {label: lang==='en'?'개루프 극점 (−)':'개루프 극점 (−)', val: rl.poles.map(p=>p.toFixed(0)).join(', ')+' rad/s', col:CL.crit},
                {label: lang==='en'?'개루프 영점 (−)':'개루프 영점 (−)', val: rl.zeros.map(z=>z.toFixed(0)).join(', ')+' rad/s', col:CL.warn},
                {label: lang==='en'?'Analysis':'설명', val: lang==='en'?'Red dashed = stability boundary (Im axis)':'빨간 점선 = 안정성 경계 (허수축)', col:COLORS.textMuted},
              ].map(i=>(
                <div key={i.label} style={SC.card}>
                  <div style={{fontSize:'10px',color:COLORS.textMuted}}>{i.label}</div>
                  <div style={{fontSize:'11px',fontWeight:700,color:i.col}}>{i.val}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ══════ [4] Z-Domain 이산 시간 분석 ══════ */}
      {sub === 'zdomain' && (() => {
        const zd = zdomain;
        const poleMarkers = zd.openLoopPoles.map(p => ({ x: p, y: 0 }));
        const zeroMarkers = [{ x: zd.zeroC, y: 0 }];
        return (
          <div>
            <CurPurpose />
            <LoopSelector />
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'12px'}}>
              {[
                {label: lang==='en'?'Sample Time':'샘플 주기', val:`${(zd.T_s*1e6).toFixed(0)} µs`, col:CL.blue},
                {label: lang==='en'?'PI zero z':'PI 영점 z', val: zd.zeroC.toFixed(4), col:CL.warn},
                {label: lang==='en'?'Plant pole z':'플랜트 극점 z', val: zd.poleP.toFixed(4), col:CL.err},
                {label: lang==='en'?'OL gain':'개루프 이득 (dB)', val: zd.gainP.toExponential(3), col:CL.ok},
              ].map(i=>(
                <div key={i.label} style={SC.kpi}>
                  <div style={{fontSize:'9px',color:COLORS.textMuted}}>{i.label}</div>
                  <div style={{fontSize:'13px',fontWeight:800,color:i.col}}>{i.val}</div>
                </div>
              ))}
            </div>

            {/* Z-plane poles/zeros */}
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px'}}>
              {lang==='en'?'Z-plane: Open-Loop Poles (×) and Zeros (○)':'Z 평면: 개루프 극점(×) 및 영점(○)'}
            </div>
            <CB width="100%" height={260} chartId="CL4">
              <ScatterChart margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                <XAxis type="number" dataKey="re" domain={[-1.5,1.5]} stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:'Re(z)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}} />
                <YAxis allowDataOverflow type="number" dataKey="im" domain={[-1.5,1.5]} stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:'Im(z)',angle:-90,position:'insideLeft',dx:10,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}} />
                {/* Unit circle */}
                <Scatter data={zd.unitCircle} fill="none" line={{stroke:`${CL.blue}55`,strokeWidth:1,strokeDasharray:'3 3'}} shape={()=>null} name={lang==='en'?'단위원 (−)':'단위원 (−)'} />
                {/* OL poles */}
                <Scatter data={poleMarkers} fill={CL.crit} shape={(props)=>{
                  const{cx,cy}=props;
                  return <g><line x1={cx-8} y1={cy-8} x2={cx+8} y2={cy+8} stroke={CL.crit} strokeWidth={2.5}/><line x1={cx+8} y1={cy-8} x2={cx-8} y2={cy+8} stroke={CL.crit} strokeWidth={2.5}/></g>;
                }} name={lang==='en'?'개루프 극점 (−)':'개루프 극점 (−)'} />
                {/* OL zeros */}
                <Scatter data={zeroMarkers} fill="none" shape={(props)=>{
                  const{cx,cy}=props;
                  return <circle cx={cx} cy={cy} r={8} stroke={CL.warn} strokeWidth={2.5} fill="none"/>;
                }} name={lang==='en'?'개루프 영점 (−)':'개루프 영점 (−)'} />
              </ScatterChart>
            </CB>

            {/* 이산 주파수 응답 */}
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px',marginTop:'12px'}}>
              {lang==='en'?'Discrete-time Frequency Response (0 → Nyquist)':'이산 시간 주파수 응답 (0 → 나이퀴스트)'}
            </div>
            <CB width="100%" height={220} chartId="CL5">
              <LineChart data={zd.freqData} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                <XAxis dataKey="f_norm" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Normalized Frequency (×fs/2)':'정규화 주파수 (×fs/2)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}}
                  tickFormatter={v=>`${(v*100).toFixed(0)}%`} />
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                label={{ value:lang==="en"?"Gain (dB)":"이득 (dB)", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT />} />
                <Legend wrapperStyle={{fontSize:'10px'}} />
                <ReferenceLine y={0} stroke={`${CL.neutral}55`} strokeWidth={1} />
                <Line type="monotone" dataKey="mag_dB" stroke={COLORS.accent}  strokeWidth={1.5} dot={false} name={lang==='en'?'개루프 이득 (dB)':'개루프 이득 (dB)'} />
                <Line type="monotone" dataKey="T_dB"   stroke={CL.ok}        strokeWidth={1.5} dot={false} name={lang==='en'?'폐루프 이득 (dB)':'폐루프 이득 (dB)'} />
              </LineChart>
            </CB>
            <div style={{marginTop:'8px',padding:'8px 12px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted}}>
              <strong style={{color:COLORS.text}}>{lang==='en'?'Stability criterion:':'안정성 기준:'}</strong>&nbsp;
              {lang==='en'
                ?'In z-domain, all CL poles must be INSIDE the unit circle |z| < 1. Plant pole z at e^(−T/τ) → always stable. PI integrator pole at z=1 (marginally stable, closed-loop must move inside).'
                :'Z 영역에서 모든 폐루프 극점이 단위원 내부 |z| < 1이어야 안정. 플랜트 극점 z=e^(−T/τ) → 항상 안정. PI 적분기 극점 z=1 (경계 안정, 폐루프 이동 필요).'}
            </div>
          </div>
        );
      })()}

      {/* ══════ [5] Anti-windup 시뮬레이션 ══════ */}
      {sub === 'antiwindup' && (() => {
        const aw = antiwindup;
        const merged = aw.withWindup.map((d, i) => ({
          t_ms: +(d.t * 1000).toFixed(3),
          y_aw:  +d.y.toFixed(5),
          u_aw:  Math.min(Math.max(d.u / aw.u_max, -1.5), 1.5),
          y_nw:  +(aw.withoutWindup[i]?.y || 0).toFixed(5),
          u_nw:  Math.min(Math.max((aw.withoutWindup[i]?.u || 0) / aw.u_max, -1.5), 1.5),
        }));
        const kpi = aw.kpi;
        return (
          <div>
            <CurPurpose />
            <LoopSelector />
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'12px'}}>
              {[
                {label:lang==='en'?'Overshoot w/ Windup':'Windup 과도 상승', val:`${kpi.overshoot_w}%`, col: kpi.overshoot_w>30?CL.err:CL.ok},
                {label:lang==='en'?'Overshoot w/ Anti-windup':'Anti-Windup 과도 상승', val:`${kpi.overshoot_n}%`, col: kpi.overshoot_n>20?CL.warn:CL.ok},
                {label:lang==='en'?'Settle time (windup)':'정착 시간 (Windup)', val: kpi.settle_w!=null?`${(kpi.settle_w*1000).toFixed(2)} ms`:'—', col:CL.blue},
                {label:lang==='en'?'Settle time (anti-wu)':'정착 시간 (Anti-WU)', val: kpi.settle_n!=null?`${(kpi.settle_n*1000).toFixed(2)} ms`:'—', col:CL.purple},
              ].map(i=>(
                <div key={i.label} style={SC.kpi}>
                  <div style={{fontSize:'9px',color:COLORS.textMuted}}>{i.label}</div>
                  <div style={{fontSize:'13px',fontWeight:800,color:i.col}}>{i.val}</div>
                </div>
              ))}
            </div>

            {/* 출력 응답 비교 */}
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px'}}>
              {lang==='en'?'Plant Output Response (Step: r = 1.0)':'플랜트 출력 응답 (스텝: r = 1.0)'}
            </div>
            <CB width="100%" height={220} chartId="CL6">
              <LineChart data={merged} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                <XAxis dataKey="t_ms" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Time (ms)':'시간 (ms)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}} />
                <YAxis allowDataOverflow stroke={COLORS.textMuted} tick={{fontSize:'9px'}} domain={[0,'auto']}
                label={{ value:lang==="en"?"Output y":"출력 y", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT />} />
                <Legend wrapperStyle={{fontSize:'10px'}} />
                <ReferenceLine y={1} stroke={`${CL.neutral}88`} strokeDasharray="3 3" />
                <Line type="monotone" dataKey="y_aw" stroke={CL.err} strokeWidth={2} dot={false} name={lang==='en'?'Windup 없음 (−)':'Windup 있음 (−)'} />
                <Line type="monotone" dataKey="y_nw" stroke={CL.ok} strokeWidth={2} dot={false} name={lang==='en'?'Anti-windup 적용 (−)':'Anti-windup 적용 (−)'} />
              </LineChart>
            </CB>

            {/* 제어 입력 비교 */}
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px',marginTop:'12px'}}>
              {lang==='en'?'Controller Output (normalized to u_max)':'제어 출력 (u_max 정규화)'}
            </div>
            <CB width="100%" height={180} chartId="CL7">
              <LineChart data={merged} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                <XAxis dataKey="t_ms" stroke={COLORS.textMuted} tick={{fontSize:'9px'}} />
                <YAxis allowDataOverflow stroke={COLORS.textMuted} tick={{fontSize:'9px'}} domain={[-1.5,1.5]}
                label={{ value:lang==="en"?"Control Output u":"제어 출력 u", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT />} />
                <Legend wrapperStyle={{fontSize:'10px'}} />
                <ReferenceLine y={1}  stroke={`${CL.warn}55`} strokeDasharray="3 3" label={{value:'+Sat',fill:CL.warn,fontSize:'9px'}} />
                <ReferenceLine y={-1} stroke={`${CL.warn}55`} strokeDasharray="3 3" label={{value:'-Sat',fill:CL.warn,fontSize:'9px'}} />
                <Line type="monotone" dataKey="u_aw" stroke={CL.err} strokeWidth={1.5} dot={false} name={lang==='en'?'u 제어입력 (−)':'u 제어입력 (−)'} />
                <Line type="monotone" dataKey="u_nw" stroke={CL.ok} strokeWidth={1.5} dot={false} name={lang==='en'?'u 제어입력 (−)':'u 제어입력 (−)'} />
              </LineChart>
            </CB>
            <div style={{marginTop:'8px',padding:'8px 12px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted}}>
              <strong style={{color:COLORS.text}}>{lang==='en'?'Anti-windup principle (Back-calculation):':'Anti-windup 원리 (역산법):'}</strong>&nbsp;
              {lang==='en'
                ?'When output saturates (|u|>u_max), the integrator accumulation is slowed by feeding back the saturation error ε_aw = u_sat − u_raw through tracking time Tt=√(Kp/Ki). This prevents excessive integral accumulation that causes large overshoot.'
                :'출력 포화(|u|>u_max) 시, 포화 오차 ε_aw = u_sat−u_raw를 추적 시간 Tt=√(Kp/Ki)를 통해 적분기로 피드백. 적분 과잉 누적을 억제하여 큰 오버슈트를 방지.'}
            </div>
          </div>
        );
      })()}

      {/* ══════ [6] 자동 PI 튜닝 ══════ */}
      {sub === 'autotuning' && (() => {
        const at = autotuning;
        const methods = ['current','zn','imc','simc','cc'];
        const methodColors = { current:CL.neutral, zn:CL.err, imc:CL.blue, simc:CL.ok, cc:CL.warn };
        const methodLabels = { current: lang==='en'?'Current (configured)':'현재 설정', zn:'Ziegler-Nichols', imc:'IMC', simc:'SIMC', cc:'Cohen-Coon' };
        const compData = methods.map(m => ({
          method: methodLabels[m], Kp: at.results[m]?.Kp||0, Ki: at.results[m]?.Ki||0,
          Ti: at.results[m]?.Ti||0, fill: methodColors[m],
        }));

        return (
          <div>
            <CurPurpose />
            <LoopSelector />
            {/* Plant parameters */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px',marginBottom:'12px'}}>
              {[
                {label: lang==='en'?'Plant gain K':'플랜트 이득 K', val: at.K_p.toExponential(3), col:CL.blue},
                {label: lang==='en'?'Time constant τ':'시정수 τ', val: at.tau_p>=1e-3?`${(at.tau_p*1000).toFixed(3)} ms`:`${at.tau_p.toExponential(3)} s`, col:CL.warn},
                {label: lang==='en'?'Dead time θ':'데드타임 θ', val: at.theta_p>=1e-6?`${(at.theta_p*1e6).toFixed(1)} µs`:`${at.theta_p.toExponential(2)} s`, col:CL.err},
              ].map(i=>(
                <div key={i.label} style={SC.kpi}>
                  <div style={{fontSize:'9px',color:COLORS.textMuted}}>{i.label}</div>
                  <div style={{fontSize:'13px',fontWeight:800,color:i.col}}>{i.val}</div>
                </div>
              ))}
            </div>

            {/* 결과 테이블 */}
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'8px'}}>
              {lang==='en'?'Tuning Results Comparison':'튜닝 결과 비교'}
            </div>
            <div style={{overflowX:'auto',marginBottom:'14px'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                <thead>
                  <tr style={{background:COLORS.border}}>
                    {['Method','Kp','Ki','Ti (s)',lang==='en'?'BW (est)':'BW (추정)'].map(h=>(
                      <th key={h} style={{padding:'6px 10px',textAlign:'left',color:COLORS.textMuted,fontWeight:600,fontSize:'10px'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {methods.map(m=>{
                    const r = at.results[m];
                    if(!r) return null;
                    const bw_hz = r.Kp / (at.tau_p * at.K_p * 2 * Math.PI);
                    return (
                      <tr key={m} style={{borderBottom:`1px solid ${COLORS.border}`,background: m===tuneMethod?`${methodColors[m]}11`:undefined}}>
                        <td style={{padding:'6px 10px',fontWeight:700,color:methodColors[m]}}>{methodLabels[m]}</td>
                        <td style={{padding:'6px 10px',fontFamily:mono,color:COLORS.text}}>{r.Kp.toFixed(4)}</td>
                        <td style={{padding:'6px 10px',fontFamily:mono,color:COLORS.text}}>{r.Ki.toFixed(2)}</td>
                        <td style={{padding:'6px 10px',fontFamily:mono,color:COLORS.text}}>{r.Ti.toFixed(5)}</td>
                        <td style={{padding:'6px 10px',fontFamily:mono,color:COLORS.textMuted}}>{fmtHz(bw_hz)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div>
                <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px'}}>Kp {lang==='en'?'Comparison':'비교'}</div>
                <CB width="100%" height={200} chartId="CL8">
                  <BarChart data={compData} margin={{ top:5, right:10, left:0, bottom:20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                    <XAxis dataKey="method" tick={{fontSize:'8px'}} stroke={COLORS.textMuted} />
                    <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                      label={{ value:lang==="en"?"Gain Kp":"게인 Kp", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                    <Tooltip content={<CT />} />
                    <Bar dataKey="Kp" name="Kp">
                      {compData.map((d,i)=><Cell key={i} fill={methodColors[methods[i]]}/>)}
                    </Bar>
                  </BarChart>
                </CB>
              </div>
              <div>
                <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px'}}>Ki {lang==='en'?'Comparison':'비교'}</div>
                <CB width="100%" height={200} chartId="CL9">
                  <BarChart data={compData} margin={{ top:5, right:10, left:0, bottom:20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                    <XAxis dataKey="method" tick={{fontSize:'8px'}} stroke={COLORS.textMuted} />
                    <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                      label={{ value:lang==="en"?"Gain Ki":"게인 Ki", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                    <Tooltip content={<CT />} />
                    <Bar dataKey="Ki" name="Ki">
                      {compData.map((d,i)=><Cell key={i} fill={methodColors[methods[i]]}/>)}
                    </Bar>
                  </BarChart>
                </CB>
              </div>
            </div>

            {/* ZN details */}
            {at.results.zn && (
              <div style={{marginTop:'10px',padding:'8px 12px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted}}>
                <strong style={{color:COLORS.text}}>Ziegler-Nichols {lang==='en'?'Details:':'상세:'}</strong>&nbsp;
                Ku = {at.results.zn.Ku} | Tu = {(at.results.zn.Tu*1000).toFixed(3)} ms | ωu = {at.results.zn.omega_u.toFixed(0)} rad/s<br/>
                {lang==='en'
                  ?'ZN provides aggressive tuning (often 30–40% overshoot). IMC/SIMC recommended for EV drive controllers.'
                  :'ZN은 공격적 튜닝 (오버슈트 30–40% 수준). EV 구동 제어기에는 IMC/SIMC 권장.'}
              </div>
            )}
          </div>
        );
      })()}

      {/* ══════ [7] 3D 토크맵 WebGL ══════ */}
      {sub === 'torque3d' && (
        <div>
        <CurPurpose />
          <div style={{fontSize:'11px',color:COLORS.textMuted,marginBottom:'10px'}}>
            {lang==='en'
              ?'3D torque surface map: IPMSM electromagnetic torque T(RPM, Iq) with power-limit region (WebGL). Auto-rotates.'
              :'3D 토크 서피스 맵: IPMSM 전자기 토크 T(RPM, Iq) + 파워 한계 영역 (WebGL). 자동 회전.'}
          </div>
          <TorqueMap3D params={effectiveParams} height={360} />
          {/* 2D 토크-RPM 슬라이스 (recharts fallback) */}
          <div style={{marginTop:'14px'}}>
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px'}}>
              {lang==='en'?'Torque-RPM Envelope (2D slice at Iq levels)':'토크-RPM 엔벨로프 (Iq 절단면 2D)'}
            </div>
            <CB width="100%" height={220} chartId="CL10">
              <LineChart margin={{ top:5, right:10, left:0, bottom:20 }}
                data={(() => {
                  const rows = [];
                  for (let i=0; i<=40; i++) {
                    const rpm = i/40 * 15000;
                    const w_m = rpm * Math.PI / 30;
                    const Tmax = effectiveParams.T_max_motor || 320;
                    const Pmax = effectiveParams.P_max_wheel || 240000;
                    const lpm  = effectiveParams.lambda_pm || 0.12;
                    const row  = { rpm };
                    [0.25,0.5,0.75,1.0].forEach(frac => {
                      const Iq = frac * (effectiveParams.I_peak||400);
                      const Te = Math.min(Tmax, 1.5*lpm*Iq);
                      row[`T_${(frac*100)|0}pct`] = +(Math.min(Te, w_m>1?Pmax/w_m:Tmax)).toFixed(1);
                    });
                    rows.push(row);
                  }
                  return rows;
                })()}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                <XAxis dataKey="rpm" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Speed (rpm)':'회전수 (rpm)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}} />
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Torque (N·m)':'토크 (N·m)',angle:-90,position:'insideLeft',dx:10,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}} />
                <Tooltip content={<CT />} />
                <Legend wrapperStyle={{fontSize:'10px'}} />
                {[{k:'T_25pct',c:CL.blue,l:'Iq=25%'},{k:'T_50pct',c:CL.ok,l:'Iq=50%'},
                  {k:'T_75pct',c:CL.warn,l:'Iq=75%'},{k:'T_100pct',c:CL.err,l:'Iq=100%'}]
                  .map(s=><Line key={s.k} type="monotone" dataKey={s.k} stroke={s.c} strokeWidth={2} dot={false} name={s.l} />)}
              </LineChart>
            </CB>
          </div>
          <div style={{marginTop:'8px',padding:'8px 12px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted}}>
            <strong style={{color:COLORS.text}}>{lang==='en'?'Map parameters:':'맵 파라미터:'}</strong>&nbsp;
            T_max = {effectiveParams.T_max_motor||320} Nm &nbsp;|&nbsp;
            P_max = {(((effectiveParams.P_max_wheel||240000)/1000)|0)} kW &nbsp;|&nbsp;
            λ_pm = {effectiveParams.lambda_pm||0.12} Wb &nbsp;|&nbsp;
            I_peak = {effectiveParams.I_peak||400} A
          </div>
        </div>
      )}

      {/* ══════ [8] MRAC — 적응 제어 ══════ */}
      {sub === 'mrac' && (() => {
        const d = mracData;
        const ss_ok = d.ss_error < 0.01;
        return (
          <div>
            <CurPurpose />
            {/* [Fix-M4] MRAC 파라미터 슬라이더 */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(190px,45vw),1fr))',gap:'0 20px',marginBottom:'12px'}}>
              <PI label={lang==='en'?'Reference Model Pole (a_m)':'기준모델 극점 (a_m)'}
                value={effectiveParams.mrac_am??5}
                onChange={v=>onParamChange&&onParamChange('mrac_am',v)}
                unit='rad/s' step={1} min={1} max={50}
                desc={lang==='en'?'Desired plant dynamics pole. Higher = faster reference model.':'기준모델 목표 극점. 클수록 빠른 응답.'}/>
              <PI label={lang==='en'?'Adapt Rate γp':'적응율 γp'}
                value={effectiveParams.mrac_gamma_p??0.5}
                onChange={v=>onParamChange&&onParamChange('mrac_gamma_p',v)}
                unit='' step={0.1} min={0.01} max={5.0}
                desc={lang==='en'?'MIT rule Kp adaptation speed. Too high → oscillation.':'MIT 규칙 Kp 적응 속도. 너무 크면 진동.'}/>
              <PI label={lang==='en'?'Adapt Rate γr':'적응율 γr'}
                value={effectiveParams.mrac_gamma_r??0.5}
                onChange={v=>onParamChange&&onParamChange('mrac_gamma_r',v)}
                unit='' step={0.1} min={0.01} max={5.0}
                desc={lang==='en'?'MIT rule Kr adaptation speed.':'MIT 규칙 Kr 적응 속도.'}/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'14px'}}>
              {[
                {label:lang==='en'?'Reference Model Pole':'기준모델 극점', val:`${d.am} rad/s`, col:CL.ok},
                {label:lang==='en'?'Plant Pole':'플랜트 극점',            val:`${d.a_plant} rad/s`, col:CL.err},
                {label:lang==='en'?'Adapt Rate γp':'적응율 γp',           val:d.gamma_p, col:CL.blue},
                {label:lang==='en'?'SS Tracking Error':'정상상태 오차',   val:d.ss_error.toFixed(5), col: ss_ok?CL.ok:CL.orange},
              ].map(k => (
                <div key={k.label} style={{...SC.kpi, borderTop:`3px solid ${k.col}`}}>
                  <div style={{fontSize:'9px',color:COLORS.textMuted}}>{k.label}</div>
                  <div style={{fontSize:'14px',fontWeight:800,color:k.col}}>{k.val}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px'}}>
              {lang==='en'?'Step Response — Reference Model vs Plant (MRAC)':'스텝 응답 — 기준모델 vs 플랜트 (MRAC)'}
            </div>
            <CB width="100%" height={220} chartId="CL11">
              <LineChart data={d.data} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="t" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Time (s)':'시간 (s)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}}/>
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{ value:lang==="en"?"Position (m)":"위치 (m)", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT/>}/>
                <Legend wrapperStyle={{fontSize:'10px'}}/>
                <Line type="monotone" dataKey="x_m"     stroke={CL.ok} strokeWidth={2} dot={false} name={lang==='en'?'기준모델 (−)':'기준모델 (−)'}/>
                <Line type="monotone" dataKey="x_plant" stroke={CL.err} strokeWidth={2} dot={false} name={lang==='en'?'플랜트 (−)':'플랜트 (−)'}/>
                <Line type="monotone" dataKey="e"       stroke={CL.warn} strokeWidth={1.5} dot={false} name={lang==='en'?'오차 (−)':'오차 (−)'} strokeDasharray="4 2"/>
              </LineChart>
            </CB>
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px',marginTop:'12px'}}>
              {lang==='en'?'Adaptive Gains Kp(t), Kr(t)':'적응 게인 Kp(t), Kr(t) 수렴'}
            </div>
            <CB width="100%" height={180} chartId="CL12">
              <LineChart data={d.data} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="t" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}/>
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{ value:lang==="en"?"Gain Value":"게인 값", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT/>}/>
                <Legend wrapperStyle={{fontSize:'10px'}}/>
                <Line type="monotone" dataKey="Kp" stroke={COLORS.isDark ? CL.purple : CL.deep_p} strokeWidth={2} dot={false} name="Kp(t)"/>
                <Line type="monotone" dataKey="Kr" stroke={CL.orange} strokeWidth={2} dot={false} name="Kr(t)"/>
              </LineChart>
            </CB>
            <div style={{marginTop:'10px',padding:'10px 14px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted,lineHeight:1.8}}>
              <strong style={{color:COLORS.text}}>📐 MRAC MIT 규칙</strong><br/>
              {lang==='en'
                ?'ẋ_m = a_m·x_m + b_m·r  (Reference)   ẋ = a·x + b·(Kp·r + Kr·x)  (Plant)   Adaptation: K̇p = −γp·e·r,  K̇r = −γr·e·x'
                :'기준모델 ẋ_m = a_m·x_m + b_m·r   플랜트 ẋ = a·x + b·(Kp·r + Kr·x)   적응법칙: K̇p = −γp·e·r, K̇r = −γr·e·x   수렴 조건: Lyapunov 안정성 (e→0 as t→∞)'}
            </div>
          </div>
        );
      })()}

      {/* ══════ [9] H∞ — 강인 제어 ══════ */}
      {sub === 'hinf' && (() => {
        const h = hinfData;
        const passCol   = h.pass ? CL.ok : CL.err;
        const rmCol     = h.robust_margin >= 1 ? CL.ok : h.robust_margin >= 0.8 ? CL.warn : CL.err;
        const gamma_eff = h.gamma_eff ?? h.gamma;
        const gamma_eff_dB = 20 * Math.log10(Math.max(gamma_eff, 1e-9));
        return (
          <div>
            <CurPurpose />

            {/* KPI 그리드 — 6개 */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px',marginBottom:'10px'}}>
              {[
                { label: 'H∞ Norm  ‖mix‖∞',           val: h.hinf_norm.toFixed(4),       col: passCol },
                { label: lang==='en'?'Eff. γ (mode-adj)':'유효 γ (모드 보정)', val: gamma_eff.toFixed(4), col: CL.blue },
                { label: lang==='en'?'Robust Margin':'강인 여유', val: h.robust_margin.toFixed(3), col: rmCol },
                { label: lang==='en'?'Stability Radius':'안정 반경', val: h.stability_radius.toFixed(4), col: CL.purple },
                { label: lang==='en'?'S peak  |S|_max':'감도 피크 |S|', val: (h.S_peak??0).toFixed(4), col: (h.S_peak??0) < (h.Ms??2) ? CL.ok:CL.err },
                { label: lang==='en'?'T peak  |T|_max':'보완감도 피크 |T|', val: (h.T_peak??0).toFixed(4), col: CL.neutral },
              ].map(k => (
                <div key={k.label} style={{...SC.kpi, borderTop:`3px solid ${k.col}`}}>
                  <div style={{fontSize:'9px',color:COLORS.textMuted}}>{k.label}</div>
                  <div style={{fontSize:'13px',fontWeight:800,color:k.col}}>{k.val}</div>
                </div>
              ))}
            </div>

            {/* PASS/FAIL 배지 + 지배항 */}
            <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'10px',flexWrap:'wrap'}}>
              <span style={{padding:'3px 10px',borderRadius:'12px',fontSize:'10px',fontWeight:700,
                background:`${passCol}22`,color:passCol,border:`1px solid ${passCol}55`}}>
                {h.pass
                  ? (lang==='en' ? '✓ H∞ PASS' : '✓ H∞ 만족')
                  : (lang==='en' ? '✗ H∞ FAIL' : '✗ H∞ 미달')}
              </span>
              {h.dominant_term && (
                <span style={{fontSize:'9px',padding:'2px 8px',borderRadius:'10px',
                  background:`${CL.purple}22`,color:CL.purple,border:`1px solid ${CL.purple}44`}}>
                  {lang==='en'?'Dominant: ':'지배항: '}{h.dominant_term}
                </span>
              )}
              <span style={{fontSize:'9px',color:COLORS.textMuted}}>
                Ms={h.Ms??2} | W1_bw={h.ws_hz??10}Hz | W2_bw={h.w2_hz??1000}Hz
              </span>
            </div>

            {/* 차트 1: 혼합 감도 (W1S / W2KS / norm) */}
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'4px'}}>
              {lang==='en'?'Weighted Norms W1·S / W2·KS / mix vs γ':'가중 노름 W1·S / W2·KS / 혼합 vs γ'}
            </div>
            <CB width="100%" height={230} chartId="CL13">
              <LineChart data={h.freqData} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="f_hz" stroke={COLORS.textMuted} tick={{fontSize:'9px'}} scale="log" type="number"
                  domain={['dataMin','dataMax']} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v.toFixed(2)}
                  label={{value:'Frequency (Hz)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}}/>
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:'Gain (dB)',angle:-90,position:'insideLeft',dx:10,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}}/>
                <Tooltip content={<CT/>}/>
                <Legend wrapperStyle={{fontSize:'10px'}}/>
                <ReferenceLine y={gamma_eff_dB} stroke={CL.warn} strokeDasharray="4 2" strokeWidth={2}
                  label={{value:`γ_eff=${gamma_eff.toFixed(2)}`,fill:CL.warn,fontSize:'9px'}}/>
                <Line type="monotone" dataKey="W1S_dB"  stroke={CL.ok} strokeWidth={2} dot={false} name="W1·S"/>
                <Line type="monotone" dataKey="W2KS_dB" stroke={CL.blue} strokeWidth={2} dot={false} name="W2·KS"/>
                <Line type="monotone" dataKey="norm_dB" stroke={COLORS.isDark?CL.purple:CL.deep_p} strokeWidth={2.5} dot={false} name={lang==='en'?'mix norm':'혼합 노름'} strokeDasharray="6 2"/>
              </LineChart>
            </CB>

            {/* 차트 2: 원시 감도 S / T / KS */}
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'4px',marginTop:'10px'}}>
              {lang==='en'?'S / T / KS — Raw Sensitivity Functions':'S / T / KS — 원시 감도 함수'}
            </div>
            <CB width="100%" height={200} chartId="CL14">
              <LineChart data={h.freqData} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="f_hz" stroke={COLORS.textMuted} tick={{fontSize:'9px'}} scale="log" type="number"
                  domain={['dataMin','dataMax']} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v.toFixed(2)}
                  label={{value:'Frequency (Hz)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}}/>
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:'Gain (dB)',angle:-90,position:'insideLeft',dx:10,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}}/>
                <Tooltip content={<CT/>}/>
                <Legend wrapperStyle={{fontSize:'10px'}}/>
                <Line type="monotone" dataKey="S_dB"  stroke={CL.ok} strokeWidth={2} dot={false} name={lang==="en"?"S (Sensitivity)":"S (감도)"}/>
                <Line type="monotone" dataKey="T_dB"  stroke={CL.err} strokeWidth={2} dot={false} name={lang==="en"?"T (Compl.)":"T (보완감도)"}/>
                <Line type="monotone" dataKey="KS_dB" stroke={CL.blue} strokeWidth={1.5} dot={false} name={lang==="en"?"KS (Control Effort)":"KS (제어 노력)"}/>
              </LineChart>
            </CB>

            {/* H∞ 파라미터 컨트롤 */}
            <div style={{marginTop:'10px',padding:'8px 12px',borderRadius:'7px',
              background:`${CL.purple}08`,border:`1px solid ${CL.purple}30`}}>
              <div style={{fontSize:9.5,fontWeight:700,color:CL.purple,marginBottom:'6px'}}>
                {lang==='en'?'H∞ Design Parameters':'H∞ 설계 파라미터'}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(190px,45vw),1fr))',gap:'0 20px'}}>
                <PI label={lang==='en'?'γ (Performance Target)':'γ (성능 목표)'}
                  value={effectiveParams.hinf_gamma??2.0} onChange={v=>onParamChange&&onParamChange('hinf_gamma',v)}
                  unit="" step={0.1} min={0.5} max={5.0}
                  desc={lang==='en'?'‖mix‖∞ < γ → H∞ PASS':'‖혼합노름‖∞ < γ이면 만족'}/>
                <PI label={lang==='en'?'Ms (Max Sensitivity Peak)':'Ms (최대 감도 피크)'}
                  value={effectiveParams.hinf_ms??2.0} onChange={v=>onParamChange&&onParamChange('hinf_ms',v)}
                  unit="" step={0.1} min={1.0} max={5.0}
                  desc={lang==='en'?'W1 DC gain = Ms. Higher → looser tracking req.':'W1 DC 게인. 클수록 저주파 요구 완화'}/>
                <PI label={lang==='en'?'W1 BW (Tracking)':'W1 대역폭 (추적)'}
                  value={effectiveParams.hinf_w1_bw??10} onChange={v=>onParamChange&&onParamChange('hinf_w1_bw',v)}
                  unit="Hz" step={1} min={1} max={100}
                  desc={lang==='en'?'Sensitivity requirement bandwidth':'감도 요구 유효 주파수 대역'}/>
                <PI label={lang==='en'?'W2 BW (Control Effort)':'W2 대역폭 (제어 노력)'}
                  value={effectiveParams.hinf_w2_bw??1000} onChange={v=>onParamChange&&onParamChange('hinf_w2_bw',v)}
                  unit="Hz" step={50} min={100} max={5000}
                  desc={lang==='en'?'High-freq control suppression BW':'고주파 제어 억제 대역'}/>
              </div>
            </div>

            {/* 수식 설명 */}
            <div style={{marginTop:'10px',padding:'10px 14px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted,lineHeight:1.9}}>
              <strong style={{color:COLORS.text}}>
                {lang==='en'?'Mixed Sensitivity H∞ Problem':'혼합 감도 H∞ 문제'}
              </strong><br/>
              {lang==='en'
                ?<>
                  min ‖[W₁·S, W₂·KS, W₃·T]‖∞ &lt; γ_eff
                  <br/>W₁(jω)=Ms·ωs/√(ω²+ωs²) [DC=Ms] · W₂(jω)=ω/(√(ω²+ω₂²)·Kp) [HF=1/Kp] · W₃=1
                  <br/>γ_eff = γ × mode_scale (SPORT×1.3, ECO×0.8, SNOW×0.7)
                  <br/>[Fix] Exact S: 1/|1+L| via complex L(jω) · W1 DC=Ms · W2 Kp-normalized
                </>
                :<>
                  min ‖[W₁·S, W₂·KS, W₃·T]‖∞ &lt; γ_eff
                  <br/>W₁(jω)=Ms·ωs/√(ω²+ωs²) [DC=Ms] · W₂(jω)=ω/(√(ω²+ω₂²)·Kp) [HF=1/Kp 정규화] · W₃=1
                  <br/>γ_eff = γ × 모드 스케일 (SPORT×1.3, ECO×0.8, SNOW×0.7)
                  <br/>[수정] S 정확 계산: 복소 L(jω) 기반 1/|1+L| · W1 저주파 게인=Ms · W2 Kp 정규화
                </>}
            </div>
          </div>
        );
      })()}

      {/* ══════ [10] MPC — 예측 제어 ══════ */}
      {sub === 'mpc' && (() => {
        const mp = mpcData;
        const lastPt = mp.data[mp.data.length-1] || {};
        return (
          <div>
            <CurPurpose />
            {/* [Fix-M4] MPC 파라미터 슬라이더 */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(190px,45vw),1fr))',gap:'0 20px',marginBottom:'12px'}}>
              <PI label={lang==='en'?'Prediction Horizon N':'예측 지평 N'}
                value={effectiveParams.mpc_N??10}
                onChange={v=>onParamChange&&onParamChange('mpc_N',Math.round(v))}
                unit='' step={1} min={2} max={30}
                desc={lang==='en'?'Future prediction steps. Larger=smoother but heavier.':'미래 예측 스텝. 클수록 부드럽지만 연산↑.'}/>
              <PI label={lang==='en'?'State Weight Q':'상태 가중 Q'}
                value={effectiveParams.mpc_q_pos??100}
                onChange={v=>onParamChange&&onParamChange('mpc_q_pos',v)}
                unit='' step={10} min={1} max={10000}
                desc={lang==='en'?'Tracking error penalty. Higher=tighter.':'추종 오차 페널티. 클수록 빠른 추종.'}/>
              <PI label={lang==='en'?'Input Weight R':'입력 가중 R'}
                value={effectiveParams.mpc_r_u??1}
                onChange={v=>onParamChange&&onParamChange('mpc_r_u',v)}
                unit='' step={0.5} min={0.1} max={100}
                desc={lang==='en'?'Control effort penalty. Higher=smoother.':'제어 노력 페널티. 클수록 부드러운 입력.'}/>
              <PI label={lang==='en'?'Input Rate Limit ΔU':'입력 변화량 제한 ΔU'}
                value={effectiveParams.mpc_dU_max??50}
                onChange={v=>onParamChange&&onParamChange('mpc_dU_max',v)}
                unit='Nm' step={5} min={5} max={200}
                desc={lang==='en'?'Max torque change per step — jerk limiting.':'스텝당 최대 토크 변화량.'}/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'14px'}}>
              {[
                {label:lang==='en'?'Prediction Horizon N':'예측 지평 N', val:mp.N, col:CL.blue},
                {label:lang==='en'?'State Weight q':'상태 가중치 q',      val:mp.q,   col:CL.ok},
                {label:lang==='en'?'Input Weight r':'입력 가중치 r',      val:mp.r_u, col:CL.warn},
                {label:lang==='en'?'SS Speed Error':'정상상태 속도 오차', val:`${mp.ss_err} km/h`, col: mp.ss_err<2?CL.ok:CL.orange},
              ].map(k => (
                <div key={k.label} style={{...SC.kpi, borderTop:`3px solid ${k.col}`}}>
                  <div style={{fontSize:'9px',color:COLORS.textMuted}}>{k.label}</div>
                  <div style={{fontSize:'14px',fontWeight:800,color:k.col}}>{k.val}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px'}}>
              {lang==='en'?'MPC Speed Tracking — 0→30 m/s Step':'MPC 속도 추종 — 0→30 m/s 스텝'}
            </div>
            <CB width="100%" height={220} chartId="CL15">
              <LineChart data={mp.data} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="t" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Time (s)':'시간 (s)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}}/>
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}} width={72}
                label={{ value:lang==="en"?"Speed (km/h)":"속도 (km/h)", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT/>}/>
                <Legend wrapperStyle={{fontSize:'10px'}}/>
                <Line type="monotone" dataKey="v_ref_kmh" stroke={CL.ok} strokeWidth={1.5} dot={false} name={lang==='en'?'목표 속도 (km/h)':'목표 속도 (km/h)'} strokeDasharray="5 3"/>
                <Line type="monotone" dataKey="v_kmh"     stroke={CL.err} strokeWidth={2}   dot={false} name={lang==='en'?'MPC 출력 (km/h)':'MPC 출력 (km/h)'}/>
              </LineChart>
            </CB>
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px',marginTop:'12px'}}>
              {lang==='en'?'Optimal Control Input u*(t) [kN]':'최적 입력 u*(t) [kN]'}
            </div>
            <CB width="100%" height={160} chartId="CL16">
              <LineChart data={mp.data} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="t" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}/>
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}} width={62}
                label={{ value:lang==="en"?"Control Force (kN)":"제어력 (kN)", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT/>}/>
                <Line type="monotone" dataKey="u_kN" stroke={CL.blue} strokeWidth={2} dot={false} name="u* (kN)"/>
              </LineChart>
            </CB>
            <div style={{marginTop:'10px',padding:'10px 14px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted,lineHeight:1.8}}>
              <strong style={{color:COLORS.text}}>📐 MPC 최적화 문제</strong><br/>
              {lang==='en'
                ?'min Σ_{k=0}^{N} [q(v_ref−v_k)² + r·ΔU_k²]   s.t. |ΔU_k| ≤ ΔU_max, |U_k| ≤ F_max   Receding horizon: solve at each step, apply only first input'
                :'min Σ_{k=0}^{N} [q(v_ref−v_k)² + r·ΔU_k²]   제약: |ΔU_k| ≤ ΔU_max, |U_k| ≤ F_max   Receding Horizon: 매 스텝 최적화 → 첫 번째 입력만 적용 → 반복'}
            </div>
          </div>
        );
      })()}

      {/* ══════ [11] d↔q 교차 커플링 해석 ══════ */}
      {sub === 'coupling' && (() => {
        const dq = dqData;
        const crit_d = dq.crit_rpm_d || '—';
        const crit_q = dq.crit_rpm_q || '—';
        return (
          <div>
            <CurPurpose />
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'14px'}}>
              {[
                {label:'Rs (Ω)',                                              val:dq.Rs.toFixed(4), col:CL.neutral},
                {label:'Ld (mH)',                                             val:(dq.Ld*1000).toFixed(2), col:CL.blue},
                {label:'Lq (mH)',                                             val:(dq.Lq*1000).toFixed(2), col:CL.err},
                {label:'λpm (Wb)',                                            val:dq.lpm.toFixed(3), col:CL.ok},
              ].map(k => (
                <div key={k.label} style={{...SC.kpi, borderTop:`3px solid ${k.col}`}}>
                  <div style={{fontSize:'9px',color:COLORS.textMuted}}>{k.label}</div>
                  <div style={{fontSize:'14px',fontWeight:800,color:k.col}}>{k.val}</div>
                </div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px',marginBottom:'14px'}}>
              {[
                {label:lang==='en'?'d-axis coupling >50% RPM':'d축 커플링 >50% 시작 RPM', val:`${crit_d} rpm`, col:CL.blue},
                {label:lang==='en'?'q-axis coupling >50% RPM':'q축 커플링 >50% 시작 RPM', val:`${crit_q} rpm`, col:CL.err},
                {label:lang==='en'?'Field Weakening RPM':'약자속 진입 RPM',               val:`${dq.fw_rpm < 99999 ? dq.fw_rpm : '—'} rpm`, col:CL.warn},
              ].map(k => (
                <div key={k.label} style={{...SC.kpi, borderTop:`3px solid ${k.col}`}}>
                  <div style={{fontSize:'9px',color:COLORS.textMuted}}>{k.label}</div>
                  <div style={{fontSize:'14px',fontWeight:800,color:k.col}}>{k.val}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px'}}>
              {lang==='en'?'Cross-Coupling Voltage Ratio (%) vs RPM':'교차 커플링 전압 비율 (%) vs RPM'}
            </div>
            <CB width="100%" height={230} chartId="CL17">
              <LineChart data={dq.freqData} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="n_rpm" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Motor Speed (rpm)':'모터 회전수 (rpm)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}}/>
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}} width={55}
                label={{ value:lang==="en"?"Compensation (%)":"보상 비율 (%)", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT/>}/>
                <Legend wrapperStyle={{fontSize:'10px'}}/>
                <ReferenceLine y={50} stroke={CL.warn} strokeDasharray="4 2"
                  label={{value:lang==="en"?'50% Decoupling Threshold':'50% 분리보상 임계',fill:CL.warn,fontSize:'9px'}}/>
                <Line type="monotone" dataKey="cr_d_pct" stroke={CL.blue} strokeWidth={2} dot={false}
                  name={lang==='en'?'d축 커플링 비율 (%)':'d축 커플링 비율 (%)'}/>
                <Line type="monotone" dataKey="cr_q_pct" stroke={CL.err} strokeWidth={2} dot={false}
                  name={lang==='en'?'q축 커플링 비율 (%)':'q축 커플링 비율 (%)'}/>
              </LineChart>
            </CB>
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px',marginTop:'12px'}}>
              {lang==='en'?'Cross-Coupling Voltage Terms (V) — Compensation Required':'교차 커플링 전압 항 (V) — 분리 보상 필요량'}
            </div>
            <CB width="100%" height={200} chartId="CL18">
              <LineChart data={dq.freqData} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="n_rpm" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}/>
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}} width={65}
                label={{ value:lang==="en"?"Voltage (V)":"전압 (V)", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT/>}/>
                <Legend wrapperStyle={{fontSize:'10px'}}/>
                <Line type="monotone" dataKey="Vd_coup" stroke={CL.blue} strokeWidth={2} dot={false}
                  name={lang==='en'?'d축 커플링 전압 (V)':'d축 커플링 전압 (V)'}/>
                <Line type="monotone" dataKey="Vq_coup" stroke={CL.err} strokeWidth={2} dot={false}
                  name={lang==='en'?'q축 커플링 전압 (V)':'q축 커플링 전압 (V)'}/>
                <Line type="monotone" dataKey="Vd_pi"   stroke={CL.blue} strokeWidth={1} dot={false}
                  name={lang==='en'?'d축 PI 전압 보상 후 (V)':'d축 PI 전압 보상 후 (V)'} strokeDasharray="4 2"/>
                <Line type="monotone" dataKey="Vq_pi"   stroke={CL.err} strokeWidth={1} dot={false}
                  name={lang==='en'?'q축 PI 전압 보상 후 (V)':'q축 PI 전압 보상 후 (V)'} strokeDasharray="4 2"/>
              </LineChart>
            </CB>
            {/* ── [DC-Dyn] DC 링크 전압 새그 → FW 진입 RPM 영향 ── */}
            {dq.fw_rpm_by_sag && (
              <div style={{marginTop:'12px',padding:'10px 14px',background:COLORS.cardBg,borderRadius:'8px',
                border:`1px solid ${COLORS.border}`}}>
                <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'8px'}}>
                  {lang==='en'?'DC Link Voltage Sag → Field Weakening RPM Shift':'DC 링크 전압 새그 → 약자속 진입 RPM 변화'}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'6px',marginBottom:'8px'}}>
                  {dq.fw_rpm_by_sag.map(s => (
                    <div key={s.sag_pct} style={{textAlign:'center',padding:'6px 4px',borderRadius:'6px',
                      background: s.sag_pct===0 ? `${CL.blue}18` : s.sag_pct<=5 ? `${CL.ok}18` : s.sag_pct<=10 ? `${CL.warn}18` : `${CL.err}18`,
                      border:`1px solid ${s.sag_pct===0?CL.blue:s.sag_pct<=5?CL.ok:s.sag_pct<=10?CL.warn:CL.err}40`}}>
                      <div style={{fontSize:'8px',color:COLORS.textMuted}}>V_dc×{(1-s.sag_pct/100).toFixed(2)}</div>
                      <div style={{fontSize:'13px',fontWeight:800,
                        color: s.sag_pct===0?CL.blue:s.sag_pct<=5?CL.ok:s.sag_pct<=10?CL.warn:CL.err}}>
                        {s.fw_rpm < 99999 ? s.fw_rpm.toLocaleString() : '—'}
                      </div>
                      <div style={{fontSize:'8px',color:COLORS.textMuted}}>rpm</div>
                      <div style={{fontSize:'8px',color:COLORS.textDim}}>{s.sag_pct===0?'정격':`−${s.sag_pct}%`}</div>
                    </div>
                  ))}
                </div>
                <div style={{fontSize:'9px',color:COLORS.textMuted,lineHeight:1.6}}>
                  ℹ️ {lang==='en'
                    ?'DC link sag reduces V_max = V_dc×fw_margin/√3, pulling FW entry RPM lower. At −10% sag, torque derating begins earlier.'
                    :'DC 링크 전압 강하 시 V_max=V_dc×fw_margin/√3 감소 → FW 진입 RPM이 앞당겨짐. −10% 새그에서 토크 디레이팅 구간 확대.'}
                </div>
              </div>
            )}

            <div style={{marginTop:'10px',padding:'10px 14px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted,lineHeight:1.8}}>
              <strong style={{color:COLORS.text}}>📐 d↔q 전압 방정식 교차 커플링</strong><br/>
              {lang==='en'
                ?'Vd = Rs·Id + Ld·dId/dt − ωe·Lq·Iq  (q→d coupling)   Vq = Rs·Iq + Lq·dIq/dt + ωe·Ld·Id + ωe·λpm  (d→q + back-EMF)   Decoupling: subtract coupling terms from PI output before modulation'
                :'Vd = Rs·Id + Ld·(dId/dt) − ωe·Lq·Iq  (q→d 커플링)   Vq = Rs·Iq + Lq·(dIq/dt) + ωe·Ld·Id + ωe·λpm  (d→q + 역기전력)   분리 보상: PI 출력에서 커플링 항을 빼고 변조기에 입력 → 독립 d/q 제어 실현'}
            </div>
          </div>
        );
      })()}


      {/* ══════ [A33 / V44] 게인 스케줄링 — 3D 보간 테이블 ══════ */}
      {sub === 'gainschedule' && (() => {
        const gs = gainSchedData;
        const nom = gs.nominal;

        // ── 3D 테이블 헬퍼 ───────────────────────────────────────
        const AXIS_META = {
          rpm:    { icon:'⚡', labelKo:'모터 속도 [RPM]',  labelEn:'Motor Speed [RPM]',
                    axis: gs.RPM_AXIS_3D,    labels: gs.RPM_LABELS_3D,    dim:'ri' },
          torque: { icon:'🔩', labelKo:'토크 [%]',         labelEn:'Torque Load [%]',
                    axis: gs.TRQ_AXIS_3D,    labels: gs.TRQ_LABELS_3D,    dim:'ti' },
          temp:   { icon:'🌡️', labelKo:'모터 온도 [°C]',   labelEn:'Motor Temp [°C]',
                    axis: gs.TMP_AXIS_3D,    labels: gs.TMP_LABELS_3D,    dim:'ki' },
        };
        const METRICS = [
          { key:'Kp_i',    col:CL.blue, labelKo:'Kp 전류루프', labelEn:'Kp Current' },
          { key:'Ki_i',    col:CL.purple, labelKo:'Ki 전류루프', labelEn:'Ki Current' },
          { key:'Kp_s',    col:CL.teal, labelKo:'Kp 속도루프', labelEn:'Kp Speed'   },
          { key:'Ki_s',    col:CL.orange, labelKo:'Ki 속도루프', labelEn:'Ki Speed'   },
          { key:'PM_i',    col:CL.warn, labelKo:'PM 전류 [°]', labelEn:'PM Current [°]' },
          { key:'BW_i_hz', col:CL.pink, labelKo:'BW 전류 [Hz]',labelEn:'BW Current [Hz]' },
        ];
        const curMetric = METRICS.find(m => m.key === gsMetric) || METRICS[0];

        // Z축 결정 (X, Y가 아닌 나머지)
        const ALL_AXES = ['rpm','torque','temp'];
        const gs3dZ = ALL_AXES.find(a => a !== gs3dX && a !== gs3dY) || 'temp';
        const zMeta  = AXIS_META[gs3dZ];
        const xMeta  = AXIS_META[gs3dX];
        const yMeta  = AXIS_META[gs3dY];
        const safeZIdx = Math.min(gs3dZIdx, zMeta.axis.length - 1);

        // 3D 격자에서 X×Y 슬라이스 추출 (Z = safeZIdx)
        function getSliceVal(xi, yi) {
          const pt = gs.grid3d.find(p =>
            p[xMeta.dim] === xi && p[yMeta.dim] === yi && p[zMeta.dim] === safeZIdx
          );
          return pt ? (pt[gsMetric] || 0) : 0;
        }

        // 전체 슬라이스 값 범위 (색상 정규화용)
        const allVals = xMeta.axis.map((_,xi) =>
          yMeta.axis.map((_,yi) => getSliceVal(xi,yi))
        ).flat();
        const vMin = Math.min(...allVals);
        const vMax = Math.max(...allVals);
        const vRange = vMax - vMin || 1;

        // 값 → 색상 (파란→초록→노랑→빨간)
        function valToColor(v) {
          const t = (v - vMin) / vRange; // 0~1
          if (t < 0.33) {
            const u = t / 0.33;
            const r = Math.round(96  + u * (52  - 96 ));
            const g = Math.round(165 + u * (211 - 165));
            const b = Math.round(250 + u * (153 - 250));
            return `rgb(${r},${g},${b})`;
          } else if (t < 0.66) {
            const u = (t - 0.33) / 0.33;
            const r = Math.round(52  + u * (250 - 52 ));
            const g = Math.round(211 + u * (204 - 211));
            const b = Math.round(153 + u * (0   - 153));
            return `rgb(${r},${g},${b})`;
          } else {
            const u = (t - 0.66) / 0.34;
            const r = Math.round(250 + u * (248 - 250));
            const g = Math.round(204 + u * (113 - 204));
            const b = Math.round(0   + u * (113 - 0  ));
            return `rgb(${r},${g},${b})`;
          }
        }

        // 보간 프로브
        const probeResult = gs.trilinearInterp(gsProbeRpm, gsProbeTrq, gsProbeTemp);

        // classic view 용
        const axisData = gs[gsAxis];
        const axisLabel = gsAxis==='rpm'
          ? (lang==='en'?'Motor Speed [RPM]':'모터 속도 [RPM]')
          : gsAxis==='torque'
          ? (lang==='en'?'Torque [Nm]':'토크 [Nm]')
          : (lang==='en'?'Motor Temp (°C)':'모터 온도 (°C)');
        const pct = (val, nom_val) => nom_val ? +((val/nom_val-1)*100).toFixed(1) : 0;

        return (
          <div>
            <CurPurpose />
            {/* 설명 배너 */}
            <div style={{marginBottom:'10px', padding:'8px 12px', background:`${CL.teal}10`,
              borderRadius:'6px', border:'1px solid #10b98130', fontSize:'10px', color:COLORS.textDim, lineHeight:1.7}}>
              🧊 {lang==='en'
                ? '[V44] 3D Interpolation Gain Table — RPM × Torque × Temperature trilinear interpolation. Combined saturation effects: Lq(rpm,load), Rs(T).'
                : '[V44] 3D 복합 보간 게인 테이블 — RPM × 토크 × 온도 3선형 보간. Lq(rpm·부하 포화), Rs(T) 복합 반영.'}<br/>
              💡 {lang==='en'
                ? 'Current loop: Kp = τ/(K·(λ+θ)),  Ki = Kp/τ  |  Speed loop: IMC inner BW dependency'
                : '전류 루프: Kp = τ/(K·(λ+θ)),  Ki = Kp/τ  |  속도 루프: 내부 BW 기반 IMC'}
            </div>

            {/* 뷰 토글 */}
            <div style={{display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap', alignItems:'center'}}>
              {[
                { id:'3d',      icon:'🧊', labelKo:'3D 보간 테이블', labelEn:'3D Interp Table' },
                { id:'classic', icon:'📊', labelKo:'클래식 차트',    labelEn:'Classic Charts'  },
              ].map(v => (
                <button key={v.id} onClick={()=>setGsView(v.id)}
                  style={{...SC.subBtn(gsView===v.id), padding:'5px 14px', fontSize:'11px'}}>
                  {v.icon} {lang==='en'?v.labelEn:v.labelKo}
                </button>
              ))}
            </div>

            {/* 공칭 KPI — 공통 */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'8px', marginBottom:'14px'}}>
              {[
                {label:lang==='en'?'Nominal Kp (cur)':'공칭 Kp (전류)', val:nom.Kp_i, col:CL.blue},
                {label:lang==='en'?'Nominal Ki (cur)':'공칭 Ki (전류)', val:nom.Ki_i, col:CL.purple},
                {label:lang==='en'?'Nominal Kp (spd)':'공칭 Kp (속도)', val:nom.Kp_s, col:CL.teal},
                {label:lang==='en'?'Nominal Ki (spd)':'공칭 Ki (속도)', val:nom.Ki_s, col:CL.orange},
              ].map(k=>(
                <div key={k.label} style={{...SC.kpi, borderTop:`3px solid ${k.col}`}}>
                  <div style={{fontSize:'9px', color:COLORS.textMuted}}>{k.label}</div>
                  <div style={{fontSize:'14px', fontWeight:800, color:k.col}}>{k.val}</div>
                </div>
              ))}
            </div>

            {/* ════ 3D 보간 테이블 뷰 ════ */}
            {gsView === '3d' && (
              <div>
                {/* ① 축 배치 컨트롤 */}
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'12px'}}>
                  {/* X축 선택 */}
                  <div style={{background:COLORS.cardBg, borderRadius:'6px', padding:'8px 10px',
                    border:`1px solid ${COLORS.border}`}}>
                    <div style={{fontSize:'9px', color:COLORS.textMuted, marginBottom:'4px'}}>
                      {lang==='en'?'X-Axis (columns)':'X축 (열)'}
                    </div>
                    <div style={{display:'flex', gap:'4px', flexWrap:'wrap'}}>
                      {ALL_AXES.map(a => (
                        <button key={a} onClick={()=>{ if(a!==gs3dY){ setGs3dX(a); } }}
                          style={{...SC.subBtn(gs3dX===a), padding:'3px 8px', fontSize:'10px',
                            opacity: a===gs3dY ? 0.4 : 1 }}>
                          {AXIS_META[a].icon} {lang==='en'
                            ? a==='rpm'?'RPM': a==='torque'?'Torq':'Temp'
                            : a==='rpm'?'속도': a==='torque'?'토크':'온도'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Y축 선택 */}
                  <div style={{background:COLORS.cardBg, borderRadius:'6px', padding:'8px 10px',
                    border:`1px solid ${COLORS.border}`}}>
                    <div style={{fontSize:'9px', color:COLORS.textMuted, marginBottom:'4px'}}>
                      {lang==='en'?'Y-Axis (rows)':'Y축 (행)'}
                    </div>
                    <div style={{display:'flex', gap:'4px', flexWrap:'wrap'}}>
                      {ALL_AXES.map(a => (
                        <button key={a} onClick={()=>{ if(a!==gs3dX){ setGs3dY(a); } }}
                          style={{...SC.subBtn(gs3dY===a), padding:'3px 8px', fontSize:'10px',
                            opacity: a===gs3dX ? 0.4 : 1 }}>
                          {AXIS_META[a].icon} {lang==='en'
                            ? a==='rpm'?'RPM': a==='torque'?'Torq':'Temp'
                            : a==='rpm'?'속도': a==='torque'?'토크':'온도'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* 표시 게인 선택 */}
                  <div style={{background:COLORS.cardBg, borderRadius:'6px', padding:'8px 10px',
                    border:`1px solid ${COLORS.border}`}}>
                    <div style={{fontSize:'9px', color:COLORS.textMuted, marginBottom:'4px'}}>
                      {lang==='en'?'Display Metric':'표시 게인 항목'}
                    </div>
                    <div style={{display:'flex', gap:'3px', flexWrap:'wrap'}}>
                      {METRICS.map(m => (
                        <button key={m.key} onClick={()=>setGsMetric(m.key)}
                          style={{...SC.subBtn(gsMetric===m.key), padding:'3px 6px', fontSize:'9px',
                            borderColor: gsMetric===m.key ? m.col : undefined,
                            color: gsMetric===m.key ? m.col : undefined}}>
                          {lang==='en'?m.labelEn:m.labelKo}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ② Z축 슬라이더 */}
                <div style={{background:COLORS.cardBg, borderRadius:'6px', padding:'8px 12px',
                  border:`1px solid ${COLORS.border}`, marginBottom:'12px'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap'}}>
                    <span style={{fontSize:'10px', color:COLORS.textMuted, minWidth:60}}>
                      {zMeta.icon} {lang==='en'?'Z-Axis (slice):':'Z축 (슬라이스):'}
                    </span>
                    <span style={{fontSize:'10px', color:COLORS.text, minWidth:80, fontWeight:700}}>
                      {lang==='en'?gs3dZ==='rpm'?'RPM':gs3dZ==='torque'?'Torque':'Temp'
                                  :gs3dZ==='rpm'?'모터 속도':gs3dZ==='torque'?'토크':'온도'}
                    </span>
                    <input type="range" min={0} max={zMeta.axis.length-1} value={safeZIdx}
                      onChange={e=>setGs3dZIdx(+e.target.value)}
                      style={{flex:1, minWidth:120, accentColor: curMetric.col}} />
                    <span style={{fontSize:'11px', fontWeight:800, color: curMetric.col, minWidth:80}}>
                      {zMeta.labels[safeZIdx]}
                    </span>
                    <div style={{display:'flex', gap:'4px'}}>
                      {zMeta.axis.map((_,i) => (
                        <button key={i} onClick={()=>setGs3dZIdx(i)}
                          style={{...SC.subBtn(i===safeZIdx), padding:'2px 6px', fontSize:'9px'}}>
                          {zMeta.labels[i]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ③ 히트맵 테이블 */}
                <div style={{fontSize:'11px', fontWeight:700, color:COLORS.text, marginBottom:'6px'}}>
                  {lang==='en'
                    ? `3D Gain Table — ${curMetric.labelEn} @ ${zMeta.labelEn}=${zMeta.labels[safeZIdx]}`
                    : `3D 게인 테이블 — ${curMetric.labelKo} @ ${zMeta.labelKo}=${zMeta.labels[safeZIdx]}`}
                </div>
                <div style={{overflowX:'auto', marginBottom:'10px'}}>
                  <table style={{borderCollapse:'collapse', fontSize:'10px', minWidth:'100%',
                    fontFamily:"'JetBrains Mono', monospace"}}>
                    <thead>
                      <tr>
                        {/* 코너 셀: Y축 레이블 \ X축 레이블 */}
                        <th style={{padding:'5px 8px', background:COLORS.panel,
                          border:`1px solid ${COLORS.border}`, fontSize:'9px',
                          color:COLORS.textMuted, textAlign:'center', whiteSpace:'nowrap'}}>
                          <span style={{color:CL.blue}}>{lang==='en'?
                            (yMeta.dim==='ri'?'RPM':yMeta.dim==='ti'?'Torq':'Temp'):
                            (yMeta.dim==='ri'?'속도':yMeta.dim==='ti'?'토크':'온도')}</span>
                          {' \\ '}
                          <span style={{color:CL.pink}}>{lang==='en'?
                            (xMeta.dim==='ri'?'RPM':xMeta.dim==='ti'?'Torq':'Temp'):
                            (xMeta.dim==='ri'?'속도':xMeta.dim==='ti'?'토크':'온도')}</span>
                        </th>
                        {xMeta.labels.map((xl,xi) => (
                          <th key={xi} style={{padding:'4px 8px', background:COLORS.panel,
                            border:`1px solid ${COLORS.border}`, color:CL.pink,
                            fontWeight:700, fontSize:'9px', textAlign:'center', whiteSpace:'nowrap'}}>
                            {xl}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {yMeta.labels.map((yl, yi) => (
                        <tr key={yi}>
                          <td style={{padding:'4px 8px', background:COLORS.panel,
                            border:`1px solid ${COLORS.border}`, color:CL.blue,
                            fontWeight:700, fontSize:'9px', whiteSpace:'nowrap'}}>
                            {yl}
                          </td>
                          {xMeta.labels.map((_xl, xi) => {
                            const val = getSliceVal(xi, yi);
                            const bg  = valToColor(val);
                            const t   = (val - vMin) / vRange;
                            const txtCol = t > 0.55 ? (COLORS.isDark ? COLORS.text : COLORS.panel) : CL.neutral;
                            const nomVal = nom[gsMetric] || 1;
                            const delta  = nomVal ? +((val/nomVal-1)*100).toFixed(1) : 0;
                            const pmOk   = gsMetric==='PM_i' ? val>=45 : null;
                            return (
                              <td key={xi} style={{
                                padding:'5px 7px', border:`1px solid ${COLORS.border}`,
                                background: bg, textAlign:'center',
                                position:'relative', minWidth:64,
                              }}>
                                <div style={{fontSize:'11px', fontWeight:800, color:txtCol}}>
                                  {val}
                                </div>
                                <div style={{fontSize:'8px', color: delta>=0
                                    ? (t>0.55?COLORS.isDark ? CL.ok : CL.deep_g:CL.ok)
                                    : (t>0.55?COLORS.isDark ? CL.err : COLORS.thermalRed:CL.err),
                                  fontWeight:600}}>
                                  {delta>=0?'+':''}{delta}%
                                </div>
                                {gsMetric==='PM_i' && (
                                  <div style={{fontSize:'7px', color: pmOk ? CL.deep_g : CL.crit,
                                    position:'absolute', top:2, right:3, fontWeight:800}}>
                                    {pmOk?'✓':'⚠'}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ④ 색상 범례 */}
                <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'14px',
                  padding:'6px 10px', background:COLORS.cardBg, borderRadius:'6px',
                  border:`1px solid ${COLORS.border}`}}>
                  <span style={{fontSize:'9px', color:COLORS.textMuted, whiteSpace:'nowrap'}}>
                    {lang==='en'?'Low':'최소'} {(+vMin.toFixed(3))}
                  </span>
                  <div style={{flex:1, height:12, borderRadius:'4px',
                    background:'linear-gradient(to right, #60a5fa, #34d399, #facc15, #f87171)'}}>
                  </div>
                  <span style={{fontSize:'9px', color:COLORS.textMuted, whiteSpace:'nowrap'}}>
                    {lang==='en'?'High':'최대'} {(+vMax.toFixed(3))}
                  </span>
                  <span style={{fontSize:'9px', color:COLORS.textMuted, marginLeft:'8px'}}>
                    {curMetric.labelKo} | Δ% vs 공칭({+(nom[gsMetric]||0).toFixed(3)})
                  </span>
                </div>

                {/* ⑤ 3선형 보간 프로브 */}
                <div style={{background:COLORS.cardBg, borderRadius:'8px', padding:'12px 14px',
                  border:`1px solid ${COLORS.border}`, marginBottom:'14px'}}>
                  <div style={{fontSize:'11px', fontWeight:700, color:COLORS.text, marginBottom:'10px'}}>
                    🔍 {lang==='en'?'Trilinear Interpolation Probe':'3선형 보간 프로브'}
                    <span style={{fontSize:'9px', color:COLORS.textMuted, marginLeft:'8px'}}>
                      {lang==='en'?'— enter any (RPM, Torque%, Temp) → interpolated gains':'— 임의 (RPM, 토크%, 온도) 입력 → 보간 게인'}
                    </span>
                  </div>
                  {/* 슬라이더 3개 */}
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'12px'}}>
                    <div>
                      <div style={{fontSize:'9px', color:CL.blue, marginBottom:'3px'}}>
                        ⚡ {lang==='en'?'Motor Speed':'모터 속도'}: <strong>{gsProbeRpm} rpm</strong>
                      </div>
                      <input type="range" min={500} max={13000} step={100} value={gsProbeRpm}
                        onChange={e=>setGsProbeRpm(+e.target.value)}
                        style={{width:'100%', accentColor:CL.blue}} />
                      <div style={{display:'flex', justifyContent:'space-between', fontSize:'8px', color:COLORS.textMuted}}>
                        <span>500</span><span>13000 rpm</span>
                      </div>
                    </div>
                    <div>
                      <div style={{fontSize:'9px', color:CL.teal, marginBottom:'3px'}}>
                        🔩 {lang==='en'?'Torque Fraction':'토크 분율'}: <strong>{(gsProbeTrq*100).toFixed(0)}%</strong>
                        {' ≈ '}<strong>{+(gs.T_max*(gsProbeTrq||0.5)).toFixed(0)} Nm</strong>
                      </div>
                      <input type="range" min={0} max={100} step={1} value={Math.round(gsProbeTrq*100)}
                        onChange={e=>setGsProbeTrq(+e.target.value/100)}
                        style={{width:'100%', accentColor:CL.teal}} />
                      <div style={{display:'flex', justifyContent:'space-between', fontSize:'8px', color:COLORS.textMuted}}>
                        <span>0%</span><span>100%</span>
                      </div>
                    </div>
                    <div>
                      <div style={{fontSize:'9px', color:CL.orange, marginBottom:'3px'}}>
                        🌡️ {lang==='en'?'Motor Temp':'모터 온도'}: <strong>{gsProbeTemp}°C</strong>
                      </div>
                      <input type="range" min={-20} max={150} step={5} value={gsProbeTemp}
                        onChange={e=>setGsProbeTemp(+e.target.value)}
                        style={{width:'100%', accentColor:CL.orange}} />
                      <div style={{display:'flex', justifyContent:'space-between', fontSize:'8px', color:COLORS.textMuted}}>
                        <span>-20°C</span><span>150°C</span>
                      </div>
                    </div>
                  </div>
                  {/* 보간 결과 KPI */}
                  <div style={{display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:'6px'}}>
                    {METRICS.map(m => {
                      const val  = probeResult[m.key] || 0;
                      const nomV = nom[m.key] || 1;
                      const dlt  = nomV ? +((val/nomV-1)*100).toFixed(1) : 0;
                      return (
                        <div key={m.key} style={{
                          ...SC.kpi, borderTop:`2px solid ${m.col}`,
                          background: gsMetric===m.key ? `${m.col}18` : undefined,
                          outline: gsMetric===m.key ? `1px solid ${m.col}` : undefined,
                        }}>
                          <div style={{fontSize:'8px', color:COLORS.textMuted}}>
                            {lang==='en'?m.labelEn:m.labelKo}
                          </div>
                          <div style={{fontSize:'13px', fontWeight:800, color:m.col}}>{val}</div>
                          <div style={{fontSize:'8px', color: dlt>=0?CL.ok:CL.err, fontWeight:600}}>
                            {dlt>=0?'+':''}{dlt}% vs nom
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* PM 경고 */}
                  {probeResult.PM_i < 45 && (
                    <div style={{marginTop:'8px', padding:'5px 10px', background:`${CL.crit}18`,
                      border:'1px solid #ef4444', borderRadius:'4px', fontSize:'10px', color:CL.err}}>
                      ⚠ {lang==='en'
                        ? `Phase Margin ${probeResult.PM_i}° < 45° — gains need review at this operating point`
                        : `위상여유 ${probeResult.PM_i}° < 45° — 해당 운전 지점에서 게인 재검토 필요`}
                    </div>
                  )}
                  {probeResult.sep < 5 && (
                    <div style={{marginTop:'6px', padding:'5px 10px', background:`${CL.warn}18`,
                      border:`1px solid ${CL.warn}`, borderRadius:'4px', fontSize:'10px', color:CL.warn}}>
                      ⚠ {lang==='en'
                        ? `Loop separation ${probeResult.sep}× < 5× — speed/current loop coupling risk`
                        : `루프 분리비 ${probeResult.sep}× < 5× — 속도/전류 루프 결합 위험`}
                    </div>
                  )}
                </div>

                {/* ⑥ 설계 가이드 */}
                <div style={{padding:'10px 14px', background:COLORS.cardBg, borderRadius:'8px',
                  fontSize:'10px', color:COLORS.textMuted, lineHeight:1.8,
                  border:`1px solid ${COLORS.border}`}}>
                  <strong style={{color:COLORS.text}}>
                    📐 {lang==='en'?'3D Gain Scheduling Design Guide':'3D 게인 스케줄링 설계 가이드'}
                  </strong><br/>
                  {lang==='en'
                    ?`① RPM axis: Lq saturation at high speed → τ↓ → Kp must decrease to maintain PM.
② Torque axis: heavy load → Iq↑ → additional Lq saturation → Ki adjustment needed.
③ Temperature axis: Rs(T)=Rs₀·[1+α(T−T_ref)] — cold startup Rs↓ → Kp can increase (BW limit applies).
④ 3D trilinear interpolation gives smooth bumpless gain transitions in real controller.
⑤ Red/orange cells in PM table = Phase Margin < 45° → retuning required at those points.`
                    :`① RPM 축: 고속에서 Lq 포화 → τ↓ → PM 유지를 위해 Kp 감소 필요.
② 토크 축: 고부하 → Iq↑ → 추가 Lq 포화 → Ki 보정 필요.
③ 온도 축: Rs(T)=Rs₀·[1+α(T−T_ref)] — 저온 시 Rs↓ → Kp 증가 가능 (BW 한계 주의).
④ 3선형 보간으로 실제 컨트롤러에서 부드러운 게인 전환(Bumpless Transfer) 구현.
⑤ PM 표의 빨간/주황 셀 = 위상여유 < 45° → 해당 지점 재튜닝 필요.`}
                </div>
              </div>
            )}

            {/* ════ 클래식 차트 뷰 ════ */}
            {gsView === 'classic' && (
              <div>
                {/* 축 선택 */}
                <div style={{display:'flex', gap:'6px', marginBottom:'12px', flexWrap:'wrap'}}>
                  <span style={{fontSize:'11px', color:COLORS.textMuted, marginRight:'4px', lineHeight:'28px'}}>
                    {lang==='en'?'Schedule Axis:':'스케줄 축:'}
                  </span>
                  {[
                    { id:'rpm',    icon:'⚡', labelKo:'속도(RPM)', labelEn:'Speed (rpm)' },
                    { id:'torque', icon:'🔩', labelKo:'토크(부하)', labelEn:'Torque' },
                    { id:'temp',   icon:'🌡️', labelKo:'온도',       labelEn:'Temperature' },
                  ].map(a => (
                    <button key={a.id} onClick={()=>setGsAxis(a.id)}
                      style={{...SC.subBtn(gsAxis===a.id), padding:'5px 14px'}}>
                      {a.icon} {lang==='en'?a.labelEn:a.labelKo}
                    </button>
                  ))}
                </div>

                {/* Kp 차트 */}
                <div style={{fontSize:'11px', fontWeight:600, color:COLORS.text, marginBottom:'6px'}}>
                  {lang==='en'?'Optimal Kp by Zone':'구간별 최적 Kp'}
                </div>
                <CB width="100%" height={220} chartId="CL19">
                  <ComposedChart data={axisData} margin={{top:5, right:14, left:0, bottom:16}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                    <XAxis dataKey="label" stroke={COLORS.textMuted} tick={{fontSize:'8px'}}
                      label={{value:axisLabel, position:'insideBottom', offset:-12, fontSize:'9px', fill:COLORS.textMuted}}/>
                    <YAxis yAxisId="cur" stroke={CL.blue} tick={{fontSize:'9px'}} width={55}
                      label={{value:lang==='en'?'Current P-Gain (A/A)':'전류 P-게인 (A/A)', angle:-90, position:'insideLeft', dx:10, fontSize:'10px', fill:CL.blue, style:{textAnchor:'middle'}}}/>
                    <YAxis yAxisId="spd" orientation="right" stroke={CL.teal} tick={{fontSize:'9px'}} width={55}
                      label={{value:lang==='en'?'Speed P-Gain (Nm·s/rad)':'속도 P-게인 (Nm·s/rad)', angle:90, position:'insideRight', dx:2, fontSize:'9px', fill:CL.teal, style:{textAnchor:'middle'}}}/>
                    <Tooltip content={<CT/>}/>
                    <Legend wrapperStyle={{fontSize:'10px'}}/>
                    <Bar yAxisId="cur" dataKey="Kp_i" fill={CL.blue} fillOpacity={0.7} name={lang==="en"?"Kp Current Loop":"Kp 전류루프"} radius={[3,3,0,0]}/>
                    <Line yAxisId="spd" type="monotone" dataKey="Kp_s" stroke={CL.teal} strokeWidth={2} dot={{r:4}} name={lang==="en"?"Kp Speed Loop":"Kp 속도루프"}/>
                    <ReferenceLine yAxisId="cur" y={nom.Kp_i} stroke={CL.blue} strokeDasharray="4 2"
                      label={{value:`Nom ${nom.Kp_i}`, fill:CL.blue, fontSize:'8px'}}/>
                  </ComposedChart>
                </CB>

                {/* Ki 차트 */}
                <div style={{fontSize:'11px', fontWeight:600, color:COLORS.text, marginBottom:'6px', marginTop:'14px'}}>
                  {lang==='en'?'Optimal Ki by Zone':'구간별 최적 Ki'}
                </div>
                <CB width="100%" height={200} chartId="CL20">
                  <ComposedChart data={axisData} margin={{top:5, right:14, left:0, bottom:16}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                    <XAxis dataKey="label" stroke={COLORS.textMuted} tick={{fontSize:'8px'}}
                      label={{value:axisLabel, position:'insideBottom', offset:-12, fontSize:'9px', fill:COLORS.textMuted}}/>
                    <YAxis yAxisId="cur" stroke={COLORS.isDark ? CL.purple : CL.deep_p} tick={{fontSize:'9px'}} width={55}
                      label={{value:lang==='en'?'Current I-Gain (A/A·s)':'전류 I-게인 (A/A·s)', angle:-90, position:'insideLeft', dx:10, fontSize:'10px', fill:CL.purple, style:{textAnchor:'middle'}}}/>
                    <YAxis yAxisId="spd" orientation="right" stroke={CL.orange} tick={{fontSize:'9px'}} width={60}
                      label={{value:lang==='en'?'Speed I-Gain (Nm/rad)':'속도 I-게인 (Nm/rad)', angle:90, position:'insideRight', dx:2, fontSize:'9px', fill:CL.orange, style:{textAnchor:'middle'}}}/>
                    <Tooltip content={<CT/>}/>
                    <Legend wrapperStyle={{fontSize:'10px'}}/>
                    <Bar yAxisId="cur" dataKey="Ki_i" fill={CL.purple} fillOpacity={0.7} name={lang==="en"?"Ki Current Loop":"Ki 전류루프"} radius={[3,3,0,0]}/>
                    <Line yAxisId="spd" type="monotone" dataKey="Ki_s" stroke={CL.orange} strokeWidth={2} dot={{r:4}} name={lang==="en"?"Ki Speed Loop":"Ki 속도루프"}/>
                    <ReferenceLine yAxisId="cur" y={nom.Ki_i} stroke={COLORS.isDark ? CL.purple : CL.deep_p} strokeDasharray="4 2"
                      label={{value:`Nom ${nom.Ki_i}`, fill:CL.purple, fontSize:'8px'}}/>
                  </ComposedChart>
                </CB>

                {/* BW & PM 차트 */}
                <div style={{fontSize:'11px', fontWeight:600, color:COLORS.text, marginBottom:'6px', marginTop:'14px'}}>
                  {lang==='en'?'Bandwidth & Phase Margin by Zone':'구간별 대역폭 & 위상 여유'}
                </div>
                <CB width="100%" height={200} chartId="CL21">
                  <ComposedChart data={axisData} margin={{top:5, right:14, left:0, bottom:16}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                    <XAxis dataKey="label" stroke={COLORS.textMuted} tick={{fontSize:'8px'}}
                      label={{value:axisLabel, position:'insideBottom', offset:-12, fontSize:'9px', fill:COLORS.textMuted}}/>
                    <YAxis yAxisId="bw" stroke={CL.pink} tick={{fontSize:'9px'}} width={55}
                      label={{value:lang==='en'?'Bandwidth (Hz)':'대역폭 (Hz)', angle:-90, position:'insideLeft', dx:10, fontSize:'10px', fill:CL.pink, style:{textAnchor:'middle'}}}/>
                    <YAxis yAxisId="pm" orientation="right" stroke={CL.warn} tick={{fontSize:'9px'}} width={45}
                      label={{value:lang==='en'?'Phase Margin (°)':'위상여유 (°)', angle:90, position:'insideRight', dx:2, fontSize:'9px', fill:CL.warn, style:{textAnchor:'middle'}}}/>
                    <Tooltip content={<CT/>}/>
                    <Legend wrapperStyle={{fontSize:'10px'}}/>
                    <Bar yAxisId="bw" dataKey="BW_i_hz" fill={CL.pink} fillOpacity={0.65} name={lang==="en"?"Current BW [Hz]":"전류 BW [Hz]"} radius={[3,3,0,0]}/>
                    <Line yAxisId="pm" type="monotone" dataKey="PM_i" stroke={CL.warn} strokeWidth={2} dot={{r:3}} name={lang==="en"?"Current PM (°)":"전류 PM (°)"}/>
                    <ReferenceLine yAxisId="pm" y={45} stroke="${CL.warn}88" strokeDasharray="4 2"
                      label={{value:lang==="en"?'45° (Recommended)':'45° (권장)', fill:CL.warn, fontSize:'8px'}}/>
                  </ComposedChart>
                </CB>

                {/* ─ 구간별 상세 테이블 ─ */}
                <div style={{fontSize:'11px', fontWeight:600, color:COLORS.text, marginBottom:'8px', marginTop:'14px'}}>
                  {lang==='en'?'Detailed Schedule Table':'구간별 상세 게인표'}
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:'10px', fontFamily:"'JetBrains Mono', monospace"}}>
                    <thead>
                      <tr style={{background:COLORS.panel}}>
                        {[lang==='en'?'Zone':'구간', axisLabel, 'Kp_i', 'Ki_i',
                          lang==='en'?'BW_i [Hz]':'전류 BW', 'PM_i (°)',
                          'Kp_s', 'Ki_s', lang==='en'?'Separation':'분리비',
                          lang==='en'?'Status':'상태']
                          .map(h=>(
                            <th key={h} style={{padding:'5px 8px', color:COLORS.textMuted, fontWeight:600, textAlign:'center',
                              borderBottom:`1px solid ${COLORS.border}`, whiteSpace:'nowrap'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {axisData.map((row, i) => {
                        const pm_ok  = row.PM_i >= 45;
                        const sep_ok = row.sep  >= 5;
                        const ok = pm_ok && sep_ok;
                        const kp_d = pct(row.Kp_i, nom.Kp_i);
                        const ki_d = pct(row.Ki_i, nom.Ki_i);
                        const axisUnit = gsAxis==='rpm'?' rpm': gsAxis==='torque'?' Nm':'°C';
                        return (
                          <tr key={i} style={{background: i%2===0 ? COLORS.bg : COLORS.cardBg}}>
                            <td style={{padding:'4px 8px', color:COLORS.text, fontWeight:600}}>{row.label}</td>
                            <td style={{padding:'4px 8px', color:COLORS.textMuted, textAlign:'right'}}>
                              {gsAxis==='rpm' ? row.rpm?.toLocaleString() : gsAxis==='torque' ? row.T_Nm : row.T_deg}
                              {axisUnit}
                            </td>
                            <td style={{padding:'4px 8px', color:CL.blue, textAlign:'right'}}>
                              {row.Kp_i}
                              <span style={{fontSize:'8px', color: kp_d>=0?CL.ok:CL.err, marginLeft:'3px'}}>
                                {kp_d>=0?'+':''}{kp_d}%
                              </span>
                            </td>
                            <td style={{padding:'4px 8px', color:CL.purple, textAlign:'right'}}>
                              {row.Ki_i}
                              <span style={{fontSize:'8px', color: ki_d>=0?CL.ok:CL.err, marginLeft:'3px'}}>
                                {ki_d>=0?'+':''}{ki_d}%
                              </span>
                            </td>
                            <td style={{padding:'4px 8px', color:CL.pink, textAlign:'right'}}>{row.BW_i_hz} Hz</td>
                            <td style={{padding:'4px 8px', textAlign:'right',
                              color: row.PM_i>=60?CL.ok: row.PM_i>=45?CL.warn:CL.err, fontWeight:700}}>
                              {row.PM_i}°
                            </td>
                            <td style={{padding:'4px 8px', color:CL.teal, textAlign:'right'}}>{row.Kp_s}</td>
                            <td style={{padding:'4px 8px', color:CL.orange, textAlign:'right'}}>{row.Ki_s}</td>
                            <td style={{padding:'4px 8px', textAlign:'right',
                              color: row.sep>=5?CL.ok:CL.err, fontWeight:700}}>{row.sep}×</td>
                            <td style={{padding:'4px 8px', textAlign:'center'}}>
                              <span style={SC.badge(ok?CL.ok:CL.crit, ok)}>
                                {ok ? '✓ OK' : (lang==='en'?'⚠ Review':'⚠ 검토')}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ─ 클래식 설계 가이드 ─ */}
                <div style={{marginTop:'14px', padding:'10px 14px', background:COLORS.cardBg, borderRadius:'8px',
                  fontSize:'10px', color:COLORS.textMuted, lineHeight:1.8,
                  border:`1px solid ${COLORS.border}`}}>
                  <strong style={{color:COLORS.text}}>
                    {lang==='en'?'📐 Gain Scheduling Design Guide (Classic)':'📐 게인 스케줄링 설계 가이드 (클래식)'}
                  </strong><br/>
                  {lang==='en'
                    ?'① Zones where PM < 45° or separation < 5× need different gains.\n② High RPM: Lq saturation → τ↓ → Kp must decrease to maintain PM.\n③ Low temp: Rs↓ → higher Kp possible, but watch BW vs switching freq.\n④ Switch to 3D Table view for combined RPM×Torque×Temp interpolation.'
                    :'① PM < 45° 또는 분리비 < 5× 구간은 별도 게인 필요.\n② 고속: Lq 포화 → τ↓ → PM 유지를 위해 Kp 감소 필요.\n③ 저온: Rs↓ → Kp 증가 가능, 단 BW/스위칭 주파수 주의.\n④ 3D 테이블 뷰로 전환 시 RPM×토크×온도 복합 보간 확인 가능.'}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ══════ [12] 스키드 패드 정상 선회 시뮬 ══════ */}
      {sub === 'skidpad' && (() => {
        const sd = skidData;
        const feasible = sd.results.filter(r=>r.feasible);
        const saturated = sd.results.filter(r=>!r.feasible);
        return (
          <div>
            <CurPurpose />
            {/* 반경 선택 */}
            <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'12px',flexWrap:'wrap'}}>
              <span style={{fontSize:'11px',color:COLORS.textMuted}}>{lang==='en'?'Pad Radius:':'패드 반경:'}</span>
              {[10,20,30,50,80,100].map(r => (
                <button key={r} onClick={()=>setSkidR(r)}
                  style={{...SC.subBtn(skidR===r)}}>{r}m</button>
              ))}
              <span style={{fontSize:'11px',color:COLORS.textMuted,marginLeft:'8px'}}>{lang==='en'?`R = ${skidR}m`:`반경 R = ${skidR}m`}</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'14px'}}>
              {[
                {label:lang==='en'?'Max Feasible Speed':'최대 선회 속도', val:`${sd.v_max_kmh} km/h`, col:CL.ok},
                {label:lang==='en'?'Max Lateral G':'최대 횡G',           val:`${sd.ay_max_g.toFixed(3)} g`, col:CL.orange},
                {label:lang==='en'?'Understeer Gradient Ku':'언더스티어 구배', val:sd.Ku.toFixed(4), col:CL.blue},
                {label:lang==='en'?'Handling Balance':'핸들링 특성',     val:sd.handling, col: sd.handling==='Neutral Steer'?CL.ok: sd.handling==='Understeer'?CL.blue:CL.err},
              ].map(k => (
                <div key={k.label} style={{...SC.kpi, borderTop:`3px solid ${k.col}`}}>
                  <div style={{fontSize:'9px',color:COLORS.textMuted}}>{k.label}</div>
                  <div style={{fontSize:'14px',fontWeight:800,color:k.col}}>{k.val}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px'}}>
              {lang==='en'?'Lateral Acceleration vs Speed — Skid Pad Analysis':'횡가속도 vs 속도 — 스키드패드 분석'}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:'4px',marginBottom:'4px'}}>
              <YAxisToggleBtn hidden={hideY2_rv_deg} onToggle={() => set_hideY2_rv_deg(function(p){return !p;})} label="deg (°)" lang={lang} />
            </div>
            <CB width="100%" height={240} chartId="CL22">
              <ComposedChart data={sd.results} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="v_kmh" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}
                  label={{value:lang==='en'?'Speed (km/h)':'속도 (km/h)',position:'insideBottom',offset:-14,fontSize:'9px',fill:COLORS.textMuted}}/>
                <YAxis yAxisId="g" stroke={COLORS.textMuted} tick={{fontSize:'9px'}} width={65}
                label={{ value:lang==="en"?"Lateral G (g)":"횡G (g)", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <YAxis yAxisId="deg" orientation="right" hide={hideY2_rv_deg} width={44} stroke={CL.err} tick={{fontSize:'9px'}}
                label={{ value:lang==="en"?"Slip Angle (°)":"슬립각 (°)", angle:90, position:"insideRight", dx:2, fontSize:'10px', fill:CL.err , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT/>}/>
                <Legend wrapperStyle={{fontSize:'10px'}}/>
                <ReferenceLine yAxisId="g" y={sd.ay_max_g} stroke={CL.warn} strokeDasharray="4 2"
                  label={{value:lang==='en'?'Tire limit':'타이어 한계',fill:CL.warn,fontSize:'9px'}}/>
                <Line yAxisId="g"   type="monotone" dataKey="ay_g"          data={feasible}   stroke={CL.ok} strokeWidth={2.5} dot={false} name={lang==='en'?'Lat G (feasible)':'횡G (선회 가능)'}/>
                <Line yAxisId="g"   type="monotone" dataKey="ay_g"          data={saturated}  stroke={CL.err} strokeWidth={2}   dot={false} name={lang==='en'?'Lat G (tire limit)':'횡G (타이어 한계 초과)'} strokeDasharray="4 2"/>
                <Line yAxisId="deg" type="monotone" dataKey="alpha_f_deg"                      stroke={CL.blue} strokeWidth={1.5} dot={false} name={lang==='en'?'전륜 슬립각 (°)':'전륜 슬립각 (°)'}/>
                <Line yAxisId="deg" type="monotone" dataKey="alpha_r_deg"                      stroke={CL.orange} strokeWidth={1.5} dot={false} name={lang==='en'?'후륜 슬립각 (°)':'후륜 슬립각 (°)'}/>
              </ComposedChart>
            </CB>
            <div style={{fontSize:'11px',fontWeight:600,color:COLORS.text,marginBottom:'6px',marginTop:'12px'}}>
              {lang==='en'?'Understeer Angle vs Speed (US map)':'언더스티어 각도 vs 속도 (US 맵)'}
            </div>
            <CB width="100%" height={180} chartId="CL23">
              <LineChart data={sd.results} margin={{ top:5, right:10, left:0, bottom:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                <XAxis dataKey="v_kmh" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}/>
                <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}} width={65}
                label={{ value:lang==="en"?"Slip Angle (°)":"슬립각 (°)", angle:-90, position:"insideLeft", dx:10, fontSize:'10px', fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                <Tooltip content={<CT/>}/>
                <ReferenceLine y={0} stroke={CL.ok} strokeWidth={1.5} label={{value:'Neutral',fill:CL.ok,fontSize:'9px'}}/>
                <Line type="monotone" dataKey="delta_us_deg" stroke={CL.blue} strokeWidth={2} dot={false}
                  name={lang==='en'?'언더스티어 각도 (°)':'언더스티어 각도 (°)'}/>
              </LineChart>
            </CB>
            <div style={{marginTop:'10px',padding:'10px 14px',background:COLORS.cardBg,borderRadius:'8px',fontSize:'10px',color:COLORS.textMuted,lineHeight:1.8}}>
              <strong style={{color:COLORS.text}}>📐 정상 선회 해석</strong><br/>
              {lang==='en'
                ?'Steady-state: Fy_req = m·v²/R  |  Front/rear split: lf/lr ratio  |  Understeer gradient: Ku = m/L·(lf/Car − lr/Caf)  |  Ku>0: Understeer  Ku<0: Oversteer  Ku=0: Neutral'
                :'정상 원운동 횡력: Fy = m·v²/R   전/후 분배: lf·lr 비율 사용   언더스티어 구배: Ku = m/L·(lf/Car − lr/Caf)   Ku>0: 언더스티어  Ku<0: 오버스티어  Ku=0: 중립조향   타이어 한계 도달 시 비선형 포화 (Pacejka 연동)'}
            </div>
          </div>
        );
      })()}

      {/* ══ [RevD] 멀티루프 동시 최적화 ══ */}
      {sub === 'multiloop' && (() => {
        let res; try { res = multiLoopGainOptimizer(effectiveParams); } catch(err){ res=null; }
        if (!res) return <div style={{color:COLORS.textMuted,padding:20,textAlign:'center'}}>{e('계산 중…','Computing…')}</div>;
        const { pareto: pareto_front=[], optimal: recommended } = res;
        const plotData = pareto_front.map(p=>({ BW_i:+(p.BW_i_hz||0).toFixed(1), BW_s:+(p.BW_s_hz||0).toFixed(1), PM_i:+(p.PM_i||0).toFixed(1), PM_s:+(p.PM_s||0).toFixed(1), Kp_i:p.Kp_i, Kp_s:p.Kp_s }));
        const PCOL='#8b5cf6';
        return (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div style={{gridColumn:'1/-1',display:'flex',gap:12,padding:'8px 14px',background:COLORS.cardBg,border:`1px solid ${COLORS.border}`,borderRadius:8,flexWrap:'wrap'}}>
              {recommended && [
                [e('권장 Kp_i','Rec. Kp_i'), recommended.Kp_i?.toFixed(2), '', PCOL],
                [e('권장 Kp_s','Rec. Kp_s'), recommended.Kp_s?.toFixed(1), '', PCOL],
                ['BW_i', recommended.BW_i_hz?.toFixed(1), 'Hz', CL.blue],
                ['BW_s', recommended.BW_s_hz?.toFixed(1), 'Hz', CL.ok],
                ['PM_i', recommended.PM_i?.toFixed(1), '°', recommended.PM_i>=45?CL.ok:CL.warn],
                ['PM_s', recommended.PM_s?.toFixed(1), '°', recommended.PM_s>=45?CL.ok:CL.warn],
              ].map(([lbl,val,u,c])=>(
                <div key={lbl} style={{textAlign:'center'}}>
                  <div style={{fontSize:9,color:COLORS.textMuted}}>{lbl}</div>
                  <div style={{fontSize:14,fontWeight:700,color:c}}>{val??'—'}<span style={{fontSize:9,color:COLORS.textMuted,marginLeft:1}}>{u}</span></div>
                </div>
              ))}
            </div>
            <RLGraphCard title={e('🔀 파레토 프론티어 — BW_i vs BW_s','🔀 Pareto Front — BW_i vs BW_s')} graphId="ML_PARETO">
              <ResponsiveContainer width="100%" height={210}>
                <ScatterChart margin={{top:4,right:8,left:0,bottom:20}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                  <XAxis type="number" dataKey="BW_i" name="BW_i" stroke={COLORS.textMuted} tick={{fontSize:'9px'}} label={{value:'BW_i (Hz)',position:'insideBottom',offset:0,fontSize:'10px',fill:COLORS.textMuted}}/>
                  <YAxis type="number" dataKey="BW_s" name="BW_s" stroke={COLORS.textMuted} tick={{fontSize:'9px'}} label={{value:'BW_s (Hz)',angle:-90,position:'insideLeft',dx:12,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}}/>
                  <Tooltip content={({active,payload})=>active&&payload?.length?(<div style={{background:COLORS.cardBg,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:'5px 9px',fontSize:10}}>{payload[0]?.payload&&Object.entries(payload[0].payload).map(([k,v])=><div key={k}>{k}: {typeof v==='number'?v.toFixed(2):v}</div>)}</div>):null}/>
                  <Scatter data={plotData} fill={PCOL} r={5} opacity={0.85}/>
                  {recommended && <Scatter data={[{BW_i:recommended.BW_i_hz,BW_s:recommended.BW_s_hz}]} fill="#f59e0b" r={8} name={e('권장','Rec')}/>}
                </ScatterChart>
              </ResponsiveContainer>
              <div style={{fontSize:9,color:COLORS.textMuted,marginTop:4}}>PM ≥ 45° 제약 · 분리비 ≥ 5 제약 · {pareto_front.length}{e('개 파레토 점','pts pareto')}</div>
            </RLGraphCard>
            <RLGraphCard title={e('📊 루프별 상세 파레토 테이블','📊 Loop Detail Pareto Table')} graphId="ML_TABLE">
              <div style={{overflowX:'auto',maxHeight:250,overflowY:'auto'}}>
                <table style={{borderCollapse:'collapse',fontSize:10,width:'100%'}}>
                  <thead><tr>{['Kp_i','Kp_s','BW_i','BW_s','PM_i°','PM_s°'].map(h=>(
                    <th key={h} style={{padding:'4px 7px',background:COLORS.cardBg,border:`0.5px solid ${COLORS.border}`,color:COLORS.textMuted,fontWeight:600}}>{h}</th>
                  ))}</tr></thead>
                  <tbody>{plotData.map((p,i)=>(
                    <tr key={i} style={{background:i%2===0?'transparent':COLORS.cardBg}}>
                      {[p.Kp_i?.toFixed?.(2)??p.Kp_i, p.Kp_s?.toFixed?.(1)??p.Kp_s, p.BW_i?.toFixed?.(1)??p.BW_i, p.BW_s?.toFixed?.(1)??p.BW_s, p.PM_i?.toFixed?.(1)??p.PM_i, p.PM_s?.toFixed?.(1)??p.PM_s].map((v,j)=>(
                        <td key={j} style={{padding:'3px 7px',border:`0.5px solid ${COLORS.border}`,textAlign:'center',color:COLORS.textMuted}}>{v}</td>
                      ))}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </RLGraphCard>
          </div>
        );
      })()}

      {/* ══ [RevD] 드라이브 모드 게인 맵 최적화 ══ */}
      {sub === 'drivemode' && (() => {
        let res; try { res = optimizeDriveModeGainMap(effectiveParams); } catch(err){ res=null; }
        if (!res) return <div style={{color:COLORS.textMuted,padding:20,textAlign:'center'}}>{e('계산 중…','Computing…')}</div>;
        const { modes=[] } = res;
        const barData = modes.map(m=>({ name:m.mode, Kp:m.Kp_i, Ki:m.Ki_i, BW:+(m.BW_i_hz||0).toFixed(1), PM:+(m.PM_i||0).toFixed(1) }));
        const modeColor = m=>m==='ECO'?CL.ok:m==='NORMAL'?CL.blue:m==='SPORT'?CL.orange:m==='SNOW'?'#38bdf8':'#a78bfa';
        return (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <RLGraphCard title={e('🎛️ 드라이브 모드별 최적 Kp','🎛️ Optimal Kp by Drive Mode')} graphId="DM_KP">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={barData} margin={{top:4,right:8,left:0,bottom:16}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                  <XAxis dataKey="name" stroke={COLORS.textMuted} tick={{fontSize:'10px'}}/>
                  <YAxis stroke={COLORS.textMuted} tick={{fontSize:'9px'}}/>
                  <Tooltip content={({active,payload,label})=>active&&payload?.length?(<div style={{background:COLORS.cardBg,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:'5px 9px',fontSize:10}}><div style={{fontWeight:700,marginBottom:3}}>{label}</div>{payload.map((p,i)=><div key={i} style={{color:p.fill}}>{p.name}: {p.value}</div>)}</div>):null}/>
                  <Bar dataKey="Kp" name="Kp_opt" radius={[3,3,0,0]}>
                    {barData.map((d,i)=><Cell key={i} fill={modeColor(d.name)}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </RLGraphCard>
            <RLGraphCard title={e('📊 모드별 BW & PM 요약','📊 BW & PM by Mode')} graphId="DM_BW">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={barData} margin={{top:4,right:8,left:0,bottom:16}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                  <XAxis dataKey="name" stroke={COLORS.textMuted} tick={{fontSize:'10px'}}/>
                  <YAxis yAxisId="l" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}/>
                  <YAxis yAxisId="r" orientation="right" stroke={COLORS.textMuted} tick={{fontSize:'9px'}}/>
                  <Tooltip content={({active,payload,label})=>active&&payload?.length?(<div style={{background:COLORS.cardBg,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:'5px 9px',fontSize:10}}><div style={{fontWeight:700,marginBottom:3}}>{label}</div>{payload.map((p,i)=><div key={i} style={{color:p.stroke||p.fill}}>{p.name}: {p.value}</div>)}</div>):null}/>
                  <Bar yAxisId="l" dataKey="BW" name="BW(Hz)" radius={[3,3,0,0]}>
                    {barData.map((d,i)=><Cell key={i} fill={modeColor(d.name)}/>)}
                  </Bar>
                  <Line yAxisId="r" type="monotone" dataKey="PM" name="PM(°)" stroke={CL.ok} strokeWidth={2} dot={{r:4}}/>
                </BarChart>
              </ResponsiveContainer>
              <div style={{overflowX:'auto',marginTop:8}}>
                <table style={{borderCollapse:'collapse',fontSize:10,width:'100%'}}>
                  <thead><tr>{[e('모드','Mode'),'Kp','Ki','BW(Hz)','PM(°)'].map(h=>(
                    <th key={h} style={{padding:'4px 7px',background:COLORS.cardBg,border:`0.5px solid ${COLORS.border}`,color:COLORS.textMuted,fontWeight:600}}>{h}</th>
                  ))}</tr></thead>
                  <tbody>{barData.map((d,i)=>(
                    <tr key={i}>
                      <td style={{padding:'3px 7px',border:`0.5px solid ${COLORS.border}`,fontWeight:700,color:modeColor(d.name)}}>{d.name}</td>
                      {[d.Kp?.toFixed?.(1)??d.Kp, d.Ki?.toFixed?.(0)??d.Ki, d.BW, d.PM].map((v,j)=>(
                        <td key={j} style={{padding:'3px 7px',border:`0.5px solid ${COLORS.border}`,textAlign:'center',color:COLORS.textMuted}}>{v}</td>
                      ))}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </RLGraphCard>
          </div>
        );
      })()}

      {/* ══ [RevD] 모터 동작점 맵 (MTPA/FW/MTPV 2D η 등고선) ══ */}
      {sub === 'oppoint' && (() => {
        let res; try { res = computeMotorOperatingPointMap(effectiveParams); } catch(err){ res=null; }
        if (!res) return <div style={{color:COLORS.textMuted,padding:20,textAlign:'center'}}>{e('계산 중…','Computing…')}</div>;
        const points = Array.isArray(res) ? res : [];
        // 레짐별 산점도 데이터
        const mtpa  = points.filter(p=>p.regime==='mtpa');
        const fw    = points.filter(p=>p.regime==='field_weakening_vc' || p.regime==='current_limited');
        const mtpv  = points.filter(p=>p.regime==='mtpv');
        const toPlot = arr=>arr.map(p=>({ spd:+(p.omega_m*60/(2*Math.PI)).toFixed(0), trq:+p.T_actual.toFixed(1), eta:+(p.eta_elec*100).toFixed(1), Vu:+(p.V_util||0).toFixed(2) }));
        const regimeColor = {MTPA:CL.ok, FW:CL.orange, MTPV:CL.pink};
        return (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <RLGraphCard title={e('🗺️ 동작점 맵 — MTPA/FW/MTPV 레짐','🗺️ Operating Point Map — MTPA/FW/MTPV')} graphId="OP_MAP">
              <div style={{fontSize:9,color:COLORS.textMuted,marginBottom:6}}>
                {e('속도-토크 격자 → Id/Iq/regime/η/V_util 산출 · WLTP 범위 커버','Speed-torque grid → Id/Iq/regime/η/V_util · covers WLTP range')}
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <ScatterChart margin={{top:4,right:8,left:0,bottom:20}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                  <XAxis type="number" dataKey="spd" name={e('회전수','RPM')} stroke={COLORS.textMuted} tick={{fontSize:'9px'}} label={{value:'Speed (RPM)',position:'insideBottom',offset:0,fontSize:'10px',fill:COLORS.textMuted}}/>
                  <YAxis type="number" dataKey="trq" name={e('토크','Torque')} stroke={COLORS.textMuted} tick={{fontSize:'9px'}} label={{value:'Torque (Nm)',angle:-90,position:'insideLeft',dx:12,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}}/>
                  <Tooltip content={({active,payload})=>active&&payload?.length?(<div style={{background:COLORS.cardBg,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:'5px 9px',fontSize:10}}>{payload[0]?.payload&&[['RPM','spd'],['Nm','trq'],['η%','eta'],['V_util','Vu']].map(([u,k])=><div key={k}>{k}: {payload[0].payload[k]}{u}</div>)}</div>):null}/>
                  <Scatter data={toPlot(mtpa)}  name="MTPA" fill={regimeColor.MTPA}  r={3} opacity={0.8}/>
                  <Scatter data={toPlot(fw)}    name="FW"   fill={regimeColor.FW}    r={3} opacity={0.8}/>
                  <Scatter data={toPlot(mtpv)}  name="MTPV" fill={regimeColor.MTPV}  r={3} opacity={0.8}/>
                  <Legend wrapperStyle={{fontSize:'10px'}}/>
                </ScatterChart>
              </ResponsiveContainer>
            </RLGraphCard>
            <RLGraphCard title={e('⚡ 레짐별 평균 효율 비교','⚡ Avg Efficiency by Regime')} graphId="OP_ETA">
              {(() => {
                const regimes = [{name:'MTPA',pts:mtpa},{name:'FW',pts:fw},{name:'MTPV',pts:mtpv}].filter(r=>r.pts.length>0);
                const etaBar = regimes.map(r=>({ name:r.name, eta:+(r.pts.reduce((s,p)=>s+(p.eta_elec||0),0)/r.pts.length*100).toFixed(1), count:r.pts.length }));
                return (
                  <>
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={etaBar} margin={{top:4,right:8,left:0,bottom:4}}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine}/>
                        <XAxis dataKey="name" stroke={COLORS.textMuted} tick={{fontSize:'10px'}}/>
                        <YAxis domain={[80,100]} stroke={COLORS.textMuted} tick={{fontSize:'9px'}} label={{value:'η(%)',angle:-90,position:'insideLeft',dx:12,fontSize:'10px',fill:COLORS.textMuted,style:{textAnchor:'middle'}}}/>
                        <Tooltip content={({active,payload,label})=>active&&payload?.length?(<div style={{background:COLORS.cardBg,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:'5px 9px',fontSize:10}}><div style={{fontWeight:700}}>{label}</div>{payload.map((p,i)=><div key={i}>{p.name}: {p.value}%</div>)}</div>):null}/>
                        <Bar dataKey="eta" name="η(%)" radius={[3,3,0,0]}>
                          {etaBar.map((d,i)=><Cell key={i} fill={regimeColor[d.name]||CL.blue}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:8,fontSize:10}}>
                      {etaBar.map(r=>(
                        <div key={r.name} style={{background:COLORS.cardBg,border:`0.5px solid ${COLORS.border}`,borderRadius:6,padding:'4px 9px',textAlign:'center'}}>
                          <div style={{fontSize:9,color:regimeColor[r.name]||CL.blue,fontWeight:700}}>{r.name}</div>
                          <div style={{fontSize:13,fontWeight:700,color:COLORS.text}}>{r.eta}%</div>
                          <div style={{fontSize:9,color:COLORS.textMuted}}>{r.count}{e('점','pts')}</div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </RLGraphCard>
          </div>
        );
      })()}
    </div>
  );
}

// ── CLFitParamPanel — 제어 루프 탭 전용 적합 파라미터 & 영향 ──────────────
function CLFitParamPanel({ params, COLORS, lang }) {
  const e  = (ko, en) => lang === 'en' ? en : ko;
  const mn = '"JetBrains Mono","Fira Code",monospace';
  const [open, setOpen] = useState(false);
  const accent = '#8b5cf6';

  const PARAM_ROWS = [
    {
      name: e('Q축 전류 비례 게인',   'Q-axis Current Kp'),
      sym:  'Kp_iq', unit: '—',
      val:  String(params.Kp_iq  ?? 3.02),
      desc: e('Iq(토크) 루프 응답 속도 결정. BW_i ≈ Kp/L — 클수록 빠른 토크 추종, 잡음 민감도↑',
              'Sets Iq (torque) loop bandwidth: BW_i ≈ Kp/L — higher = faster torque tracking, more noise sensitivity'),
    },
    {
      name: e('Q축 전류 적분 게인',   'Q-axis Current Ki'),
      sym:  'Ki_iq', unit: '—',
      val:  String(params.Ki_iq  ?? 75),
      desc: e('정상 상태 Iq 오차 제거 속도. Kp/Ki = τ_i = L/R 대입 시 최적 소거',
              'Eliminates steady-state Iq error. Optimal: Kp/Ki = τ_i = L/R (pole-zero cancellation)'),
    },
    {
      name: e('속도 루프 비례 게인',   'Speed Loop Kp'),
      sym:  'Kp_spd', unit: '—',
      val:  String(params.Kp_spd ?? 143.4),
      desc: e('속도 추종 응답성 결정. 분리비(BW_s/BW_i) ≥ 5× 유지 필요',
              'Sets speed tracking responsiveness. Separation ratio (BW_s/BW_i) must be ≥ 5×'),
    },
    {
      name: e('속도 루프 적분 게인',   'Speed Loop Ki'),
      sym:  'Ki_spd', unit: '—',
      val:  String(params.Ki_spd ?? 3003),
      desc: e('부하 외란·관성 변화 제거. 과도하면 Anti-windup 없이 오버슈트 급증',
              'Rejects load disturbances and inertia variation. Excessive Ki causes overshoot without anti-windup'),
    },
    {
      name: e('고정자 저항',           'Stator Resistance Rs'),
      sym:  'Rs',     unit: 'Ω',
      val:  String(params.Rs    ?? 0.015),
      desc: e('IMC/ZN 모델 식별 파라미터 K_p = 1/R. 온도 상승 시 저항↑ → BW 자동 감소',
              'Plant model parameter for IMC/ZN: K_p = 1/R. Rs rises with temp → BW decreases automatically'),
    },
    {
      name: e('d축 인덕턴스 Ld',       'd-axis Inductance Ld'),
      sym:  'Kp_id',  unit: 'H',
      val:  String(params.Ld    ?? 0.0003),
      desc: e('d축 전류 루프 시정수 τ_d = Ld/Rs 결정. 자속 약화 영역에서 포화 → Ld 감소',
              'Sets d-axis time constant τ_d = Ld/Rs. Saturates in field-weakening → effective Ld decreases'),
    },
    {
      name: e('q축 인덕턴스 Lq',       'q-axis Inductance Lq'),
      sym:  'Lq',     unit: 'H',
      val:  String(params.Lq    ?? 0.0006),
      desc: e('교차 커플링 전압 ωe·Lq·Iq. 고속·고토크에서 d↔q 분리 보상 필수 구간 결정',
              'Cross-coupling voltage ωe·Lq·Iq. Determines zones requiring d↔q feedforward decoupling at high speed/torque'),
    },
    {
      name: e('제어 샘플링 주기',       'Control Sampling Period'),
      sym:  'ctrl_dt', unit: 'μs',
      val:  ((params.ctrl_dt ?? 1e-4) * 1e6).toFixed(0),
      desc: e('이산 안정성(z-domain) 결정. Z 극점이 단위원 외부로 이동하면 발산 — ctrl_dt ≤ 1/(10·BW_i) 권장',
              'Determines discrete-domain stability (z-plane). If z-poles exit unit circle → divergence. Recommend ctrl_dt ≤ 1/(10·BW_i)'),
    },
    {
      name: e('모터 관성 모멘트',       'Motor Inertia J'),
      sym:  'J_motor', unit: 'kg·m²',
      val:  String(params.J_motor ?? 0.05),
      desc: e('속도 루프 플랜트 모델 τ_mech = J/B — J↑ → 속도 루프 느려짐, Kp_spd 재튜닝 필요',
              'Mechanical time constant τ_mech = J/B — higher J slows speed loop, requires Kp_spd re-tuning'),
    },
    {
      name: e('드라이브 모드',          'Drive Mode'),
      sym:  'drive_mode', unit: '—',
      val:  String(params.drive_mode ?? 'NORMAL'),
      desc: e('ECO→NORMAL→SPORT: Kp/Ki에 스케일 팩터 자동 적용. SPORT는 높은 게인 → 위상 여유 감소 위험',
              'ECO→NORMAL→SPORT: automatically applies scale factors to Kp/Ki. SPORT = high gains → phase margin may drop'),
    },
  ];

  const EFFECT_ROWS = [
    {
      param: e('Kp_iq / Kp_id (전류 Kp) ↑', 'Current Kp (Kp_iq / Kp_id) ↑'),
      pos: e('전류 루프 대역폭↑ → 토크 응답 빠름, 고속 정밀 제어 유리', 'Current BW↑ → faster torque response, better high-speed precision control'),
      neg: e('잡음·PWM 리플 증폭 → 전류 왜곡, 위상 여유 감소. PM < 30° 위험', 'Amplifies noise/PWM ripple → current distortion, phase margin drops. Risk: PM < 30°'),
    },
    {
      param: e('Ki_iq / Ki_id (전류 Ki) ↑', 'Current Ki (Ki_iq / Ki_id) ↑'),
      pos: e('정상 상태 Iq 오차 신속 제거, 저속 토크 정밀도↑', 'Faster steady-state Iq error elimination, better low-speed torque precision'),
      neg: e('적분 포화 시 Anti-windup 없으면 오버슈트 급증 → 과전류 트립 위험', 'Without anti-windup: integrator windup causes overshoot → overcurrent trip risk'),
    },
    {
      param: e('Kp_spd (속도 Kp) ↑', 'Speed Kp ↑'),
      pos: e('속도 추종 응답성↑, 외란 억제 향상, 급가감속 성능 개선', 'Better speed tracking, improved disturbance rejection, sharper acceleration'),
      neg: e('BW 분리비(BW_s/BW_i) 5× 초과 위험 → 내부 전류 루프 포화, 시스템 불안정', 'BW separation ratio may fall below 5× → inner current loop saturation, instability'),
    },
    {
      param: e('Ki_spd (속도 Ki) ↑', 'Speed Ki ↑'),
      pos: e('부하 외란·경사로·맞바람 속도 복원력↑, 정상 상태 속도 오차 제거', 'Faster recovery from load disturbances (grade/headwind), eliminates steady-state speed error'),
      neg: e('적분 포화 → Anti-windup 미적용 시 오버슈트·헌팅 발생, NVH 악화', 'Integrator windup without anti-windup → overshoot/hunting, NVH worsens'),
    },
    {
      param: e('ctrl_dt (샘플링 주기) ↓', 'ctrl_dt (Sampling Period) ↓'),
      pos: e('Z-domain 안정 여유↑, 이산화 오차 감소, 고주파 외란 대응 개선', 'Larger Z-domain stability margin, reduced discretisation error, better high-freq disturbance rejection'),
      neg: e('연산 부하↑, DSP 처리 시간 제약. 지나치게 짧으면 타이밍 지터로 BW 저하 역효과', 'Higher CPU load, DSP timing constraints. Excessively short period causes jitter, reducing effective BW'),
    },
    {
      param: e('Rs (고정자 저항) ↑ (온도 상승)', 'Rs ↑ (temperature rise)'),
      pos: e('해당 없음 — Rs 상승은 파라미터 드리프트로 불이익만 존재', 'N/A — Rs increase is a drift condition with no benefit'),
      neg: e('IMC 모델 K_p = 1/R 오차 → 자동 튜닝 추천 Kp 과소 → BW 저하, 응답 느려짐', 'IMC model K_p = 1/R error → auto-tuned Kp under-estimates → BW drops, sluggish response'),
    },
    {
      param: e('드라이브 모드 SPORT', 'Drive Mode: SPORT'),
      pos: e('Kp/Ki 스케일↑ → 날카로운 가속 응답, 고속 토크 벡터링 성능 향상', 'Higher Kp/Ki scale → sharp acceleration response, improved torque vectoring at high speed'),
      neg: e('위상 여유 감소 (PM 40° 미만 가능) → Nyquist 불안정 영역 근접, SPORT + 저온 Rs는 이중 위험', 'Phase margin may drop below 40° → approaching Nyquist instability. SPORT + cold Rs = double risk'),
    },
  ];

  return (
    <div style={{ marginBottom: '10px' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: open ? `${accent}20` : COLORS.cardBg,
          border: `1.5px solid ${open ? accent : COLORS.border}`,
          borderRadius: open ? '8px 8px 0 0' : 8,
          padding: '7px 16px', cursor: 'pointer',
          color: open ? accent : COLORS.text,
          fontWeight: 700, fontSize: '12px',
          width: '100%', justifyContent: 'space-between',
          transition: 'all 0.15s',
        }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '15px' }}>🎛️</span>
          <span>{e('적합 파라미터 / 영향', 'Suitable Parameters / Influence')}</span>
          <span style={{ fontSize: '10px', fontWeight: 400, color: COLORS.textMuted }}>
            {e('제어 루프 — PI 게인 · 모터 상수 · 샘플링', 'Control Loop — PI Gains · Motor Constants · Sampling')}
          </span>
        </span>
        <span style={{ fontSize: '14px', color: accent }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          background: COLORS.panel,
          border: `1.5px solid ${accent}40`,
          borderTop: 'none', borderRadius: '0 0 8px 8px',
          padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '14px',
        }}>
          {/* ── 적합 파라미터 테이블 ── */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 800, color: accent, marginBottom: '8px' }}>
              📐 {e('적합 파라미터', 'Suitable Parameters')}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ background: COLORS.cardBg }}>
                    {(lang === 'en'
                      ? ['Parameter', 'Symbol', 'Unit', 'Current Value', 'Physical Meaning']
                      : ['파라미터명', '심볼', '단위', '현재값', '설명 및 물리적 의미']
                    ).map(h => (
                      <th key={h} style={{
                        padding: '5px 10px', textAlign: 'left', color: COLORS.textMuted,
                        borderBottom: `1px solid ${COLORS.border}`, fontWeight: 600, whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PARAM_ROWS.map((p, i) => (
                    <tr key={i} style={{
                      background: i % 2 === 0 ? 'transparent' : COLORS.cardBg,
                      borderBottom: `1px solid ${COLORS.border}20`,
                    }}>
                      <td style={{ padding: '6px 10px', color: COLORS.text,    fontWeight: 600,  whiteSpace: 'nowrap' }}>{p.name}</td>
                      <td style={{ padding: '6px 10px', color: accent,         fontFamily: mn,   fontWeight: 700, whiteSpace: 'nowrap' }}>{p.sym}</td>
                      <td style={{ padding: '6px 10px', color: COLORS.textMuted, whiteSpace: 'nowrap' }}>{p.unit}</td>
                      <td style={{ padding: '6px 10px', color: COLORS.accent,  fontFamily: mn,   fontWeight: 600, whiteSpace: 'nowrap' }}>{p.val}</td>
                      <td style={{ padding: '6px 10px', color: COLORS.textMuted, lineHeight: 1.5, maxWidth: 340 }}>{p.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── 타 섹션에 미치는 영향 ── */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 800, color: accent, marginBottom: '8px' }}>
              ⚡ {e('타 섹션에 미치는 영향', 'Cross-Section Influence')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {EFFECT_ROWS.map((ef, i) => (
                <div key={i} style={{ border: `1px solid ${COLORS.border}`, borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{
                    background: `${accent}15`, padding: '5px 12px',
                    borderBottom: `1px solid ${accent}30`,
                    fontSize: '11px', fontWeight: 700, color: accent, fontFamily: mn,
                  }}>
                    {ef.param}
                  </div>
                  <div style={{ display: 'flex', gap: 0 }}>
                    <div style={{ flex: 1, padding: '6px 12px', borderRight: `1px solid ${COLORS.border}` }}>
                      <div style={{ fontSize: '9px', fontWeight: 700, color: '#22c55e', marginBottom: '3px' }}>
                        ✅ {e('긍정 영향', 'Positive')}
                      </div>
                      <div style={{ fontSize: '10px', color: COLORS.text, lineHeight: 1.5 }}>{ef.pos}</div>
                    </div>
                    <div style={{ flex: 1, padding: '6px 12px' }}>
                      <div style={{ fontSize: '9px', fontWeight: 700, color: '#f59e0b', marginBottom: '3px' }}>
                        ⚠️ {e('부정 영향 / 트레이드오프', 'Trade-off')}
                      </div>
                      <div style={{ fontSize: '10px', color: COLORS.text, lineHeight: 1.5 }}>{ef.neg}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CLFitParamPanel_PLACEHOLDER ──
export {
  ControlLoopTabContent,
};